// ============================================================
// ClientSearchView.tsx
// Phase 2b — main-area takeover for the sidebar's "Client Search"
// zone. Mirrors the Settings.tsx pattern: full-screen wrapper with
// its own AppHeader, ← Back left-slot, rendered by App.tsx when
// useCasevoStore.showClientSearch is true.
//
// Two internal modes driven by local `view` state:
//   - 'search' — search input + debounced results list (≥2 chars)
//   - 'detail' — selected client overview with cases + 3 placeholder
//                sections (Notes / Tasks / History)
//
// Backend constraints worth knowing:
//   - Search filter (clientController.getClients) is an OR across
//     forename / surname / niNumber / mainContactNumber /
//     addressPostcode. clientNumber search is NOT supported today
//     — flagged as a Reuben task.
//   - getCasesForClient is currently a placeholder returning []
//     (Case.clientId not yet wired). The CASES section renders the
//     empty state today; it'll activate when Reuben ships the link.
// ============================================================
import { refusalText } from '../utils/serverError'
import { useLivePermissions } from '../hooks/useLivePermissions'
import { canDeleteCases } from '../utils/casePermissions'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCasevoStore } from '../store/useCasevoStore'
import { goBack, initialPlaceParam, navigateTo, navigateWithin, usePlaceParam } from '../navigation/navigate'
import { getTaskByIdApi, fetchCaseById, caseOpenErrorMessage, createCaseApi, getCasesForClientApi, getClientByIdApi, listTasksApi, listActivitiesApi, deleteActivityApi, fetchDocumentSearchApi, requestDownloadUrlFlatApi, deleteFileFlatApi, msStatusApi, listEmailDraftsApi, deleteEmailDraftApi, getCaseBillingApi, isRestrictedClientError } from '../api/casevoApi'
import RestrictedClientPanel from '../components/clients/RestrictedClientPanel'
import SideScroll from '../components/shared/SideScroll'
import type { EmailDraft } from '../api/casevoApi'
import type { Client } from '../types/client.types'
import type { CaseLinkSummary } from '../types/case.types'
import type { Task } from '../types/tasks.types'
import type { Activity, ActivityTotal } from '../types/activity.types'
import type { DocumentSearchResult } from '../types/documentSearch.types'
import { usePresenceRoom, broadcastClientFieldEdits, useNewItemsCount } from '../hooks/usePresence'
import { presence } from '../services/presence'
import { useFirmChange } from '../utils/liveEvents'
import { flushClientAutoSave } from '../hooks/useClientAutoSave'
import { flushClientNotesAutoSave } from '../hooks/useClientNotesAutoSave'
import { formatDateUK, calculateCaseCompletion } from '../utils/caseHelpers'
import AppHeader from '../components/layout/AppHeader'
import BackButton from '../components/layout/BackButton'
import ClientDirectory from '../components/clients/ClientDirectory'
import CaseQuickViewModal from '../components/shared/CaseQuickViewModal'
import type { CaseSummary } from '../components/layout/CaseListScreen'
import type { RawCase } from '../types/case.types'
import { rawToSummary } from '../utils/caseSummary'
import { SidebarHamburger } from '../components/layout/Sidebar'
import ClientTasksTab from '../components/clients/ClientTasksTab'
import type { CaseInfo } from '../components/tasks/MyWork'
import ClientDocumentUploadModal from '../components/shared/ClientDocumentUploadModal'
import AddActivityModal, { type ActionVariantKey } from '../components/shared/AddActivityModal'
import ComposeEmailModal from '../components/shared/ComposeEmailModal'
import ChooseBillCaseModal from '../components/billing/ChooseBillCaseModal'
import ClientStatementModal from '../components/billing/ClientStatementModal'
import RaiseBillModal from '../components/billing/RaiseBillModal'
import BillView from '../components/billing/BillView'
import { errorText } from '../components/billing/billingUi'
import type { Bill, CaseBilling } from '../types/billing.types'
import { useBillEvents } from '../hooks/useBillEvents'
import ActivityViewModal from '../components/shared/ActivityViewModal'
import FavouriteStar from '../components/shared/FavouriteStar'
import type { ActionTypeValue } from '../constants/actionTypes.constants'
import { isInlineSafeMime, openUrlInTab, triggerBlobDownload } from '../utils/documentAccess'
import ConfirmDialogue from '../components/shared/ConfirmDialogue'
// Client Details — the registry-driven client-info sections (view +
// per-section edit + Edit all), reusable on the case page. SaveIndicator
// surfaces save failures from useClientAutoSave.
import ClientInfoSections from '../components/clientInfo/ClientInfoSections'
import BillEmailConsentCard from '../components/clientInfo/BillEmailConsentCard'
import MobileNumberCard, { mobileCardPropsOf } from '../components/clientInfo/MobileNumberCard'
import CaseActionsMenu, { type CaseActionsMenuState } from '../components/shared/CaseActionsMenu'
import { patchLinks, subscribeLinkRelationship } from '../components/cases/LinkRelationship'
import ClientCasesTab, { type CaseCardModel, type CaseStatusLabel, type LinkedCaseModel } from '../components/clients/ClientCasesTab'
import SaveIndicator from '../components/shared/SaveIndicator'
import ClientNotesFeed, { type NoteCaseOption } from '../components/clients/ClientNotesFeed'
import ClientActivityFeed, { type ActivityCaseOption } from '../components/clients/ClientActivityFeed'
import { caseName, caseClientName, caseRef, caseShortId, caseNameWithRef } from '../utils/caseName'
import { useCaseAccess } from '../hooks/useCaseAccess'
import { SectionNavItem, SectionNavPill, type SettingsIconName } from '../components/settings/settingsUi'
import NewCaseForClientModal from '../components/cases/NewCaseForClientModal'
import type { ClaimRoute, ClaimType } from '../utils/claimTypes'
import { refusedField } from '../hooks/fieldRefusals'

// Subset of the backend Case row used by the CASES detail section.
// We don't reuse RawCase because the getCasesForClient endpoint's
// response shape isn't formally typed (returns unknown[]) — declaring
// the four fields we actually display keeps the cast tight.
interface ClientCaseRow {
  id: string
  createdAt: string
  // Naming inputs for the shared caseName() rule (getCasesForClient selects them).
  caseName?: string | null
  defendantName?: string | null
  clientForename?: string | null
  clientSurname?: string | null
  accidentDate: string | null
  closeDate: string | null
  setupFinalised: boolean
  updatedAt: string
  // Setup progress (getCasesForClient selects it) — calculateCaseCompletion.
  completedSections?: string[]
  // Owner (getCasesForClient) — gates the Cases-tab Delete like deleteCase.
  assignedToUserId?: string | null
  // (Batch 4) size of the case's CONFIRMED same-accident group (0 = none).
  accidentGroupSize?: number
  // Callum #1: the cases THIS case is linked to (firm-wide). Populated by
  // getCasesForClient. Aggregated across the client's cases into the
  // "Linked to" section below.
  caseLinks?: CaseLinkSummary[]
}

// Server-side activity action-type filter (the feed's "Time" sub-chips).
type ActivityTypeFilter = 'all' | ActionTypeValue

// Case status helper — mirrors CaseListScreen.getStatus exactly so
// status chips read identically across the two screens.
const getStatus = (c: ClientCaseRow): { label: string; colour: string } => {
  if (c.closeDate) return { label: 'Closed', colour: 'bg-gray-100 dark:bg-casevo-border text-casevo-text-soft' }
  if (c.setupFinalised) return { label: 'Open', colour: 'bg-casevo-mint text-casevo-brand' }
  return { label: 'Draft', colour: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-300' }
}

// Display-name helper — mirrors the row label in ClientPickerModal.
// Title is optional; empty falls back to a sentinel so the heading
// never renders blank.
const displayName = (c: Client): string => {
  const namePart = [c.title, c.forename, c.surname].filter(Boolean).join(' ')
  return namePart || 'Unnamed Client'
}

// Readable labels for the client sections — used in the favourite label
// (e.g. "Travis Fiddler · Activity") so a pinned client-section reads
// clearly in the sidebar. Keys match activeClientSection.
// Client-page section nav (shared SectionNavItem/Pill). Order + labels as before.
const CLIENT_NAV: ReadonlyArray<{ key: 'notes' | 'activity' | 'details' | 'cases' | 'tasks'; label: string; icon: SettingsIconName }> = [
  { key: 'notes',    label: 'Notes',          icon: 'pen' },
  { key: 'activity', label: 'Activity',       icon: 'history' },
  { key: 'details',  label: 'Client Details', icon: 'user' },
  { key: 'cases',    label: 'Cases',          icon: 'brief' },
  { key: 'tasks',    label: 'Tasks',          icon: 'task' },
]
const CLIENT_SECTION_LABELS: Record<'details' | 'notes' | 'cases' | 'tasks' | 'activity', string> = {
  notes: 'Notes',
  activity: 'Activity',
  details: 'Client Details',
  cases: 'Cases',
  tasks: 'Tasks',
}

const SEARCH_DEBOUNCE_MS = 300
const SEARCH_MIN_CHARS = 2

// ── ACTION TOOLBAR ───────────────────────────────────────────
// Client-detail header shortcut row. Renders the solicitor's
// canonical action set in a fixed order. Stage 3b:
//
//   Core row (always visible), left→right, canonical order:
//     1  Make call     ACTIVE   variant 'call'
//     2  Take call     ACTIVE   variant 'takeCall'
//     3  Send SMS      DISABLED (no provider yet)
//     4  Send letter   ACTIVE   variant 'letter'
//     5  Send memo     ACTIVE   variant 'memo'
//     6  Send email    DISABLED (M365 project pending)
//     7  [+] overflow toggle — reveals the second row
//
//   Overflow row (visible when toolbarExpanded), left→right:
//     8  Linked action           DISABLED
//     9  Diary action            DISABLED
//    10  Post mail               DISABLED  (Matt to define)
//    11  Slip transaction        DISABLED  (Matt to define)
//    12  Economic doc search     DISABLED
//    13  Create new doc          DISABLED
//
// Active buttons route through onSelectVariant, which the parent
// wires to `setActivityVariant(<key>); setShowAddActivityModal(
// true)` — the direct-variant path from Stage 2b: NO picker
// step, straight into the typed screen with the timer auto-
// starting.
//
// Icons are inline SVGs matching the Sidebar's pattern verbatim:
// 24×24 viewBox, currentColor stroke, strokeWidth 2, round
// caps/joins, h-5 w-5 sizing. No lucide-react import — the
// project does not use an icon library. See src/components/
// layout/Sidebar.tsx ~L50–103 for the canonical style.
interface ActionToolbarProps {
  onSelectVariant: (variant: ActionVariantKey) => void
  // Microsoft Stage 3: opens the compose-email modal. Always clickable — the
  // modal handles the not-connected case with a connect prompt; `emailConnected`
  // only tunes the tooltip so a connected user sees "Send email" and an
  // unconnected one sees the connect hint.
  onSendEmail: () => void
  emailConnected: boolean
}
const ActionToolbar: React.FC<ActionToolbarProps> = ({ onSelectVariant, onSendEmail, emailConnected }) => {
  const iconClass = 'h-5 w-5'
  const activeButtonClass =
    'text-casevo-mid hover:text-casevo-forest hover:bg-casevo-mint/30 rounded p-2 focus:outline-none focus-visible:text-casevo-forest transition-colors'
  return (
    <div className="flex flex-col items-end gap-1 shrink-0">
      {/* Client actions — only the wired logging actions are shown
          (Make call · Take call · Letter · Memo). Unimplemented actions
          that were previously rendered as disabled "coming soon" stubs
          have been hidden; they can be re-added as their pillars land. */}
      <div className="flex items-center gap-1" aria-label="Client actions">
        {/* 1 · Make call — phone-handset silhouette. Lucide "phone". */}
        <button
          type="button"
          onClick={() => onSelectVariant('call')}
          title="Log a call"
          aria-label="Log a call"
          className={activeButtonClass}
        >
          <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        </button>
        {/* 2 · Take call — phone handset with the inbound-arrow
            marker in the top-right quadrant. Lucide "phone-
            incoming": the polyline + line above the phone path
            form the "arrow into a corner" glyph that makes this
            visually distinct from Make call at a glance. */}
        <button
          type="button"
          onClick={() => onSelectVariant('takeCall')}
          title="Log a take call"
          aria-label="Log a take call"
          className={activeButtonClass}
        >
          <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="16 2 16 8 22 8" />
            <line x1="22" y1="2" x2="16" y2="8" />
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        </button>
        {/* 4 · Send letter — document with text lines. Lucide
            "file-text". */}
        <button
          type="button"
          onClick={() => onSelectVariant('letter')}
          title="Log a letter"
          aria-label="Log a letter"
          className={activeButtonClass}
        >
          <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
        </button>
        {/* 5 · Send memo — pencil / note. Lucide "edit-3". */}
        <button
          type="button"
          onClick={() => onSelectVariant('memo')}
          title="Log a memo"
          aria-label="Log a memo"
          className={activeButtonClass}
        >
          <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </button>
        {/* 6 · Send email — envelope. Lucide "mail". Microsoft Stage 3: opens
            the compose modal (sends AS the user's own mailbox, logs an 'email'
            activity). Always clickable — the modal handles the not-connected
            case with a connect prompt; the tooltip reflects connection state. */}
        <button
          type="button"
          onClick={onSendEmail}
          title={emailConnected ? 'Send an email' : 'Send an email (connect Microsoft first)'}
          aria-label="Send an email"
          className={activeButtonClass}
        >
          <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 7-10 5L2 7" />
          </svg>
        </button>
      </div>
    </div>
  )
}

const ClientSearchView: React.FC = () => {
  const clients = useCasevoStore((s) => s.clients)
  // Current authenticated user — used per-row on activity entries to
  // gate Edit/Delete controls behind an author-or-admin check (mirrors
  // the backend's PATCH/DELETE guard on /api/activities/:id). Read as
  // a slice so the row .map only re-renders when authUser itself
  // changes, not on every unrelated store update.
  const authUser = useCasevoStore((s) => s.authUser)
  const clientsLoading = useCasevoStore((s) => s.clientsLoading)
  const clientsError = useCasevoStore((s) => s.clientsError)
  const loadClients = useCasevoStore((s) => s.loadClients)
  // Stage 4: "N new clients — refresh" indicator on the search results. Counts
  // remote client CREATEs (echo-suppressed). Click → re-run the current search
  // (loadClients) + reset. Most meaningful with an active search (the client
  // view is search-driven; there's no browse-all list) — see the flag in the
  // stage report. Count is approximate; the refetch is the source of truth.
  const [newClientCount, resetNewClients] = useNewItemsCount('client')
  // The search-vs-detail sub-view lives on the store rather than as
  // local React state so the sidebar's "Client Search" button can
  // reset it back to 'search' when the user re-clicks while already
  // inside the zone (otherwise showClientSearch is already true and
  // the click would be a silent no-op).
  const view = useCasevoStore((s) => s.clientSearchMode)
  // (Audit batch 3, H4) Showing the "Restricted client" page (a walled client
  // was opened — by its search stub, a link, a favourite or the URL).
  const [restrictedClient, setRestrictedClient] = useState(false)
  // Which client it was, so a live permission change can open it after all.
  const restrictedClientId = useRef<string | null>(null)
  const showRestrictedClient = (id: string): void => { restrictedClientId.current = id; setRestrictedClient(true) }
  const clientNavTick = useCasevoStore((s) => s.clientNavTick)
  useEffect(() => { setRestrictedClient(false) }, [clientNavTick])
  const setClientSearchMode = useCasevoStore((s) => s.setClientSearchMode)
  const pendingClientId = useCasevoStore((s) => s.pendingClientId)
  const setPendingClientId = useCasevoStore((s) => s.setPendingClientId)
  // Cross-view nav into TasksView. setPendingTaskId opens an existing
  // task; setPendingNewTaskClientId opens a fresh "+ New task" form
  // with the current client preselected. setShowTasks is the takeover
  // toggle — App.tsx's renderMain precedence puts Tasks above Client
  // Search, so flipping showTasks=true wins regardless. We also set
  // showClientSearch=false explicitly to mirror handleOpenCase.
  // selectedClient is lifted to the store so it survives takeover
  // unmount/remount (e.g. the Case Detail → ← Back round trip). If
  // it stayed as local useState, opening a case from this view
  // would destroy it on setShowClientSearch(false), and the back-
  // routing remount would render the AppHeader + blank body. See
  // store comment for the full rationale.
  const selectedClient = useCasevoStore((s) => s.selectedClient)
  // Presence ROOM for the client surface: `client:<id>` while a client is
  // open, null otherwise. Field presence (client.* keys) scopes to it.
  usePresenceRoom(selectedClient ? `client:${selectedClient.id}` : null)
  const setSelectedClient = useCasevoStore((s) => s.setSelectedClient)
  // Stage 2a — partial-update mutation for the editable Client Details
  // section. useClientAutoSave subscribes (via its expanded selector) and
  // PATCHes after the 1s debounce.
  const updateSelectedClient = useCasevoStore((s) => s.updateSelectedClient)
  // The per-task back-target setter — populated by handleOpenTask
  // so TasksView's ← Back returns here instead of falling through
  // to the Tasks list (mirrors handleOpenCase's caseOpenedFrom).

  // Local UI state. `clientCases` / `clientTasks` / `clientDocuments`
  // remain local — they refetch on remount via handleSelectClient
  // when selectedClient is restored. A brief "Loading..." flash per
  // section is the trade-off for not lifting all four to the store.
  const [search, setSearch] = useState('')
  const [clientCases, setClientCases] = useState<ClientCaseRow[] | null>(null)
  // (Batch 4) The client's cases this viewer CAN'T open — bare ids from
  // getCasesForClient (canAccess:false, nothing else). Shown only as locked
  // cards on the Cases tab; kept OUT of clientCases so no other consumer
  // (pickers, chips, labels, counts of openable cases) ever sees them.
  const [restrictedClientCaseIds, setRestrictedClientCaseIds] = useState<string[]>([])
  const splitClientCaseRows = (raw: unknown[]): ClientCaseRow[] => {
    const rows = raw as (ClientCaseRow & { canAccess?: boolean })[]
    setRestrictedClientCaseIds(rows.filter((r) => r.canAccess === false).map((r) => r.id))
    return rows.filter((r) => r.canAccess !== false)
  }
  const [casesError, setCasesError] = useState<string | null>(null)
  // Per-client Tasks + Documents sections. Loaded inside
  // handleSelectClient alongside cases (cases first because the
  // tasks filter needs the client's case IDs). null = loading,
  // [] = empty, populated = render the list. downloadingId mirrors
  // DocumentSearchView's per-row spinner pattern.
  const [clientTasks, setClientTasks] = useState<Task[] | null>(null)
  const [tasksError, setTasksError] = useState<string | null>(null)
  // Per-client Activity billable timeline. `clientActivities` follows the
  // null-loading / [] empty / [...] loaded pattern used for tasks +
  // documents above. `clientActivitiesTotal` is the sibling `total`
  // aggregated server-side over the same {clientId} filter — kept in a
  // separate slot with a safe {0,0,0} default so the header can always
  // read its fields without null-guarding.
  const [clientActivities, setClientActivities] = useState<Activity[] | null>(null)
  // The server aggregate is still fetched; the feed now totals the VISIBLE
  // entries itself (client-side family/date/person/search filters).
  const [, setClientActivitiesTotal] = useState<ActivityTotal>({ amountPence: 0, durationSeconds: 0, count: 0 })
  const [activityError, setActivityError] = useState<string | null>(null)
  const [clientDocuments, setClientDocuments] = useState<DocumentSearchResult[] | null>(null)
  const [documentsError, setDocumentsError] = useState<string | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  // actionError is shared between download AND delete failures —
  // both surface in the same red sticky banner above the documents
  // list. Mirrors FileList's actionError pattern.
  const [actionError, setActionError] = useState<string | null>(null)
  // Delete-flow state. confirmDeleteDoc carries the row the user
  // tapped trash on (drives the ConfirmDialogue). deletingId is the
  // in-flight DELETE call's id (drives the per-row spinner-disable
  // on DocumentRow's trash button via deleting={deletingId === doc.id}).
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState<DocumentSearchResult | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  // Upload modal open/close state. Triggered by the "+ Upload
  // document" button in the Documents section header.
  const [showUploadModal, setShowUploadModal] = useState(false)
  // Add-activity modal open/close. Mirrors showUploadModal — a
  // local boolean that the "+ New activity" header button flips
  // true and the modal flips back to false via its own onClose.
  const [showAddActivityModal, setShowAddActivityModal] = useState(false)
  // Microsoft Stage 3 — compose-email modal + connection status for the
  // toolbar's Send email button. msConnected is fetched once on mount (a cheap
  // status read); the button is always clickable (the modal prompts to connect
  // if needed), and msConnected only tunes its tooltip. emailToast surfaces a
  // brief success confirmation after a send.
  const [showComposeEmail, setShowComposeEmail] = useState(false)
  // Activity tab "Invoice" → pick the case the bill is for (ChooseBillCaseModal).
  const [pickBillCase, setPickBillCase] = useState(false)
  // Activity tab "Statement" → the client statement (ClientStatementModal).
  const [showStatement, setShowStatement] = useState(false)
  // "New invoice" stays ON the client page: the picked case's Create bill
  // (RaiseBillModal, fed by that case's billing) opens over the Activity tab,
  // and the new draft opens in place of the feed (clientBill) — the case page
  // is never opened. raisingCaseId = its billing is loading.
  const [raiseFor, setRaiseFor] = useState<{ caseId: string; name: string; ref: string; data: CaseBilling } | null>(null)
  const [raisingCaseId, setRaisingCaseId] = useState<string | null>(null)
  // Part of the place (?client=…&tab=activity&billId=…): a refresh or
  // Back/Forward re-opens it (usePlaceParam below).
  const [clientBill, setClientBill] = useState<{ id: string; initial?: Bill } | null>(() => {
    const id = initialPlaceParam('billId')
    return id ? { id } : null
  })
  const [msConnected, setMsConnected] = useState(false)
  const [emailToast, setEmailToast] = useState<string | null>(null)
  // Stage E — email drafts for the selected client. The list is scoped
  // to the current user (backend enforces req.user.userId) and filtered
  // by clientId. resumeDraft, when non-null, seeds ComposeEmailModal from
  // an existing draft (and is deleted on a successful send). Loaded when
  // the Activity section is shown; refreshed after save/send.
  const [clientDrafts, setClientDrafts] = useState<EmailDraft[]>([])
  // The client the drafts were last loaded for (the Activity feed waits on it).
  const [draftsClientId, setDraftsClientId] = useState<string | null>(null)
  const [resumeDraft, setResumeDraft] = useState<EmailDraft | null>(null)
  const loadDraftsForClient = useCallback(async (clientId: string): Promise<void> => {
    try {
      const rows = await listEmailDraftsApi({ clientId })
      setClientDrafts(rows)
    } catch {
      setClientDrafts([])
    } finally {
      setDraftsClientId(clientId)
    }
  }, [])
  useEffect(() => {
    let cancelled = false
    void msStatusApi()
      .then((s) => { if (!cancelled) setMsConnected(s.connected) })
      .catch(() => { if (!cancelled) setMsConnected(false) })
    return () => { cancelled = true }
  }, [])
  // Which action variant (if any) the CREATE-instance modal is
  // opened in. Stage 2c: null = "+ New activity" opener — the
  // modal shows a type-picker step where the user chooses
  // call/letter/memo (and the timer only starts on that choice).
  // A non-null key = direct-variant open (future toolbar) — the
  // modal skips the picker and jumps straight into the chosen
  // variant's form with the timer auto-running, exactly the 2b
  // behaviour. ALWAYS cleared on modal close AND set explicitly
  // on every open path so a stale variant can't leak across
  // opens.
  const [activityVariant, setActivityVariant] = useState<ActionVariantKey | null>(null)
  // Edit-activity state. When non-null, the edit modal (a second
  // AddActivityModal render, distinct from the create one above)
  // is open pre-filled with this row. Row-level "Edit" buttons
  // set it; the modal's onCreated / onClose clear it.
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null)
  // View-activity state. Non-null = the read-only ActivityViewModal is
  // open for this row. Set by the row's "View" affordance (shown to
  // users who can't edit); display-only, so there's no save/refetch.
  const [viewingActivity, setViewingActivity] = useState<Activity | null>(null)
  // Delete-activity state. confirmDeleteActivity carries the row
  // the user tapped delete on (drives the ConfirmDialogue).
  // deletingActivityId is the in-flight DELETE call's id (drives
  // the confirmLabel busy-toggle — mirrors the document delete
  // flow's deletingId at L178). Both cleared after the DELETE
  // resolves and the list refetches.
  const [confirmDeleteActivity, setConfirmDeleteActivity] = useState<Activity | null>(null)
  const [deletingActivityId, setDeletingActivityId] = useState<string | null>(null)
  // Client-detail sidebar nav. activeClientSection drives which
  // section panel renders on the right; the row in the left nav whose
  // key matches activeClientSection gets the active-row styling.
  // Initial value is a placeholder ('notes') — the default-landing
  // useEffect below overrides it the moment a client is selected,
  // setting 'notes' when the client has any notes content and
  // 'activity' otherwise. Mirrors the same sidebar-section pattern
  // used on Settings.tsx and AdminPage.tsx.
  const [activeClientSection, setActiveClientSection] = useState<'details' | 'notes' | 'cases' | 'tasks' | 'activity'>('notes')
  // Billing on the Activity timeline (issued bills, payments, credit notes —
  // masked server-side). Loaded only while the Activity tab is open.
  const { events: clientBillEvents, error: billEventsError, reload: reloadBillEvents } = useBillEvents(
    selectedClient && activeClientSection === 'activity' ? { clientId: selectedClient.id } : null,
  )
  // Activity section action-type filter (Stage 2). UNLIKE the Tasks
  // filter (client-side over the loaded array), this refetches
  // server-side so the footer units/£ total stays accurate — the
  // Stage-1 backend made the bundled `total` track the actionType
  // filter. 'all' passes no actionType (unfiltered); the others map
  // 1:1 to the ACTION_VARIANTS vocab.
  const [activityTypeFilter, setActivityTypeFilter] = useState<ActivityTypeFilter>('all')
  // Activity section CASE filter. '' = all cases (no caseId param);
  // otherwise the selected case's id. Combines with activityTypeFilter
  // — both are threaded to every refetch. Server-side like the type
  // filter, so the footer total tracks caseId too.
  const [activityCaseFilter, setActivityCaseFilter] = useState<string>('')
  // Activity section CORRESPONDENT filter (Stage 3). Tri-state:
  //   null  = All (no correspondentType param — default)
  //   ''    = None (untagged — activities with correspondentType === '')
  //   '<t>' = that correspondent type
  // Distinct from the others because '' is a MEANINGFUL value, so we
  // thread it as `undefined` (All) vs a string (incl. '') to the API.
  const [activityCorrespondentFilter, setActivityCorrespondentFilter] = useState<string | null>(null)
  // UNFILTERED activity snapshot for THIS client — the source for the
  // contextual correspondent chips. Kept separate from clientActivities
  // (which holds the FILTERED display list) so selecting a correspondent
  // doesn't collapse the chip list to just that one. Refreshed on client
  // open + after create/edit/delete (data mutations); NOT on filter
  // changes (filters don't change which correspondents exist).
  const [clientActivitiesAll, setClientActivitiesAll] = useState<Activity[] | null>(null)
  // Filter-refetch-in-flight flag (Stage 2 flash fix). Set true ONLY
  // for a filter-change refetch — NOT the initial client-open load,
  // which uses the clientActivities === null loading gate. Drives a
  // dim + "Updating…" treatment so the still-visible OLD (pre-filter)
  // rows can't be mistaken for the new filtered results. We keep NOT
  // null-resetting clientActivities (so no empty flash); this flag is
  // what distinguishes "stale rows, loading" from "here are results".
  const [activitiesRefetching, setActivitiesRefetching] = useState(false)
  // Activity filter dropdown (layout pass): open/close + a ref on the
  // wrapper for outside-click + Escape close — modelled on TPISection's
  // combobox (mousedown-outside handler). Presentation only; the panel
  // drives the SAME three filter states/handlers as before.

  // Autofocus the search input on mount AND when returning to search
  // mode from detail — `view` in the dep array re-triggers focus on
  // back-navigation so the cursor lands where the user expects.
  const searchInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (view === 'search') {
      searchInputRef.current?.focus()
    }
  }, [view])

  // Debounced search loader. Only fires above the minimum-char
  // threshold; below it, we deliberately do NOT call the backend
  // (the empty state will render instead). The cleanup clearTimeout
  // means each keystroke resets the timer cleanly.
  // (D12 live updates) Someone else added / changed / removed a client → the
  // search on screen re-runs.
  const [clientsRev, setClientsRev] = useState(0)
  useFirmChange(['clients'], () => setClientsRev((n) => n + 1))
  useEffect(() => {
    if (view !== 'search') return
    const trimmed = search.trim()
    if (trimmed.length < SEARCH_MIN_CHARS) return
    const timer = setTimeout(() => {
      void loadClients(trimmed)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search, view, loadClients, clientsRev])


  // Default-landing rule for the client-detail sidebar nav. When the
  // user opens a client, decide which section to show first:
  //   - clients with notes content → land on Notes (so the solicitor
  //     immediately sees what they last wrote about this person)
  //   - clients without notes → land on Activity (the placeholder
  //     section where future timeline content will live; gives a
  //     visible-but-empty landing rather than dropping the user into
  //     the editable Details form on first open)
  // Separate from the notes-seed effect above so the two concerns stay
  // independent: that one syncs notesValue with the loaded client,
  // this one chooses the visible section.
  //
  // DEP IS THE ID, NOT THE OBJECT REFERENCE. updateSelectedClient
  // (see useCasevoStore.ts:367-369) rebuilds `selectedClient` on every
  // keystroke via `{ ...state.selectedClient, ...changes }`, so
  // depending on the object reference would re-fire this effect on
  // every character typed and snap activeClientSection back to
  // 'notes' / 'activity' mid-edit. This effect must fire only on a
  // genuine client SWITCH. DO NOT regress the dep to [selectedClient]
  // (was the "typing in Client Details flips to Notes" regression).
  // The eslint-disable below stays: the body reads selectedClient.notes
  // but we deliberately don't re-run when notes changes intra-session —
  // landing is a first-open decision only.
  useEffect(() => {
    if (!selectedClient) return
    // (audit) A DIFFERENT client is about to be opened (pendingClientId — e.g.
    // a sidebar favourite from another view, with the previous client still
    // selected): don't land / consume its pending section on the OLD client;
    // this effect re-runs when the target becomes selected.
    const pendingId = useCasevoStore.getState().pendingClientId
    if (pendingId && pendingId !== selectedClient.id) return
    // Favourite restore (Stage 3): a client-section favourite click sets
    // pendingClientSection before opening the client. If present, open ON
    // that section instead of the default landing. One-shot — consumed
    // (cleared) regardless of validity so it never sticks to a later open;
    // applied only when it's a known section key. When absent, behaviour
    // is unchanged (default notes/activity landing below).
    const pendingSection = useCasevoStore.getState().pendingClientSection
    if (pendingSection) {
      useCasevoStore.getState().setPendingClientSection(null)
      if (Object.prototype.hasOwnProperty.call(CLIENT_SECTION_LABELS, pendingSection)) {
        setActiveClientSection(pendingSection as keyof typeof CLIENT_SECTION_LABELS)
        useCasevoStore.getState().setClientSectionMemo({ clientId: selectedClient.id, section: pendingSection })
        return
      }
    }
    // Returning to the SAME client (closed a task opened from a tab, Back from
    // a case, …): restore the tab the user was on rather than re-landing.
    const memo = useCasevoStore.getState().clientSectionMemo
    if (memo && memo.clientId === selectedClient.id && Object.prototype.hasOwnProperty.call(CLIENT_SECTION_LABELS, memo.section)) {
      setActiveClientSection(memo.section as keyof typeof CLIENT_SECTION_LABELS)
      return
    }
    const hasNotes = (selectedClient.notes ?? '').trim().length > 0
    const landing = hasNotes ? 'notes' : 'activity'
    setActiveClientSection(landing)
    useCasevoStore.getState().setClientSectionMemo({ clientId: selectedClient.id, section: landing })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.id])
  // Back/Forward to THIS client (already showing) on another tab: restoreView
  // seeds clientSectionMemo from the history entry — follow it.
  const clientSectionMemo = useCasevoStore((s) => s.clientSectionMemo)
  useEffect(() => {
    if (!selectedClient || !clientSectionMemo || clientSectionMemo.clientId !== selectedClient.id) return
    if (!Object.prototype.hasOwnProperty.call(CLIENT_SECTION_LABELS, clientSectionMemo.section)) return
    setActiveClientSection(clientSectionMemo.section as keyof typeof CLIENT_SECTION_LABELS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSectionMemo])
  // The open client + tab are this screen's place (store.snapshotCurrentView
  // → navigation/placeUrl: ?client=…&tab=…), kept in the URL + history by
  // the shared place sync. The bill opened on the Activity tab is part of it
  // too: Back/Forward that change it close / re-open it here.
  usePlaceParam('billId', view === 'detail' ? clientBill?.id ?? null : null, (id) => {
    setClientBill(id ? { id } : null)
    if (!id) reloadBillEvents()
  })


  // Surname-alpha sort, client-side. Backend defaults to clientNumber
  // ASC; the brief specifies surname-alpha for this view, and the
  // result sets are small (firm-scoped) so client-side reorder is
  // cheap. localeCompare handles diacritics + case folding.
  const sortedResults = useMemo(() => {
    return [...clients].sort((a, b) => {
      const aKey = (a.surname || '').toLowerCase()
      const bKey = (b.surname || '').toLowerCase()
      return aKey.localeCompare(bKey)
    })
  }, [clients])

  // Select a client → swap to detail mode + load their cases, then
  // tasks + documents in parallel. Cases come FIRST because the
  // tasks filter needs the client's case IDs (a task may be linked
  // to the client directly OR via any of their cases — we want
  // both). Tasks + documents fire in parallel via Promise.allSettled
  // so one failing doesn't block the other.
  //
  // Backend notes:
  //   - getCasesForClientApi returns the client's cases via the wired
  //     Case.clientId link (schema Task 1.3) — used for the tasks
  //     cross-ref AND the Activity section's case filter.
  //   - listTasksApi has no clientId filter on the backend, so we
  //     fetch firm-wide and filter client-side. Acceptable for small
  //     firms; add a backend filter if list size becomes a concern.
  //   - fetchDocumentSearchApi DOES accept clientId, so docs are
  //     fetched pre-filtered with no client-side work.

  // Extracted documents fetch — used by both handleSelectClient
  // (initial load) and the upload modal's success callback (refresh
  // after a successful upload). useCallback so the modal's prop
  // reference stays stable across renders, matching the pattern
  // DocumentsSection.tsx uses for its handleRefresh callback.
  // The client whose detail data is currently wanted (set by handleSelectClient).
  // Async loaders drop responses for any other client — see handleSelectClient.
  const activeClientIdRef = useRef<string | null>(null)
  const loadDocumentsForClient = useCallback(async (clientId: string): Promise<void> => {
    setClientDocuments(null)
    setDocumentsError(null)
    try {
      const docs = await fetchDocumentSearchApi({ clientId })
      if (activeClientIdRef.current && activeClientIdRef.current !== clientId) return // switched client — drop stale response
      setClientDocuments(docs)
    } catch (err) {
      if (activeClientIdRef.current && activeClientIdRef.current !== clientId) return
      const message = err instanceof Error ? err.message : 'Failed to load documents for this client'
      setDocumentsError(message)
      setClientDocuments([])
    }
  }, [])

  // Refetch just this client's activities (used by the AddActivityModal
  // onCreated callback after a successful create). Mirrors
  // loadDocumentsForClient's shape. Does NOT reset the list to null
  // first — the just-added entry lands from the server response and
  // replaces the old list in one paint, so no "Loading..." flash.
  //
  // Stage 2/3: accepts optional actionType + caseId filters (they
  // COMBINE). Both are threaded to listActivitiesApi so the server
  // narrows rows AND recomputes the bundled total over the same where
  // clause (footer units/£ stay accurate for the combined filter).
  // Omit / undefined → that filter is unapplied. Deliberately does NOT
  // null the list first, so a refetch swaps rows in one paint without
  // a "Loading..." flash (matches create/delete).
  const loadActivitiesForClient = async (
    clientId: string,
    filters?: { actionType?: string; caseId?: string; correspondentType?: string },
  ): Promise<void> => {
    try {
      const { activities, total } = await listActivitiesApi({
        clientId,
        ...(filters?.actionType ? { actionType: filters.actionType } : {}),
        ...(filters?.caseId ? { caseId: filters.caseId } : {}),
        // '' is meaningful (untagged) — thread on `!== undefined`, not truthiness.
        ...(filters?.correspondentType !== undefined ? { correspondentType: filters.correspondentType } : {}),
      })
      if (activeClientIdRef.current && activeClientIdRef.current !== clientId) return // switched client — drop stale response
      setClientActivities(activities)
      setClientActivitiesTotal(total)
      setActivityError(null) // (audit) a successful reload clears a stale error banner
    } catch (err) {
      if (activeClientIdRef.current && activeClientIdRef.current !== clientId) return
      const message = err instanceof Error ? err.message : 'Failed to load activity for this client'
      setActivityError(message)
    }
  }
  // Fetch the UNFILTERED activity set for this client and store it as
  // the correspondent-chip source (clientActivitiesAll). Called on
  // client open and after create/edit/delete — NOT on filter changes,
  // since filters don't change which correspondents exist. Failures are
  // non-fatal (chips just don't refresh); the display list has its own
  // error handling.
  const loadCorrespondentChipSource = async (clientId: string): Promise<void> => {
    try {
      const { activities } = await listActivitiesApi({ clientId })
      if (activeClientIdRef.current && activeClientIdRef.current !== clientId) return
      setClientActivitiesAll(activities)
    } catch {
      /* non-fatal — leave the existing chip source in place */
    }
  }
  // Current filter params from live state — for refetches NOT triggered
  // by a filter change (create/edit/delete). Filter-change handlers
  // pass the NEW value explicitly (setState hasn't committed yet).
  // correspondentType: null → undefined (All); '' stays '' (None).
  // ★ Live permission change: lost a money right → clear the £ on screen at
  // once, then re-read the open client's activity under the new rights.
  useLivePermissions((narrowed) => {
    // …including an open activity view and a raise-bill snapshot (both carry £).
    if (narrowed) { setClientActivities(null); setClientActivitiesAll(null); setViewingActivity(null); setRaiseFor(null) }
    if (selectedClient) void loadActivitiesForClient(selectedClient.id, currentActivityFilters())
    void recheckClientWall()
  })
  // (See all clients) The wall moved under an open screen: the search re-reads
  // (a stub becomes a card, or the reverse), the Cases tab re-reads its locks, and
  // the open client ↔ the "Restricted client" page swap. The server decides.
  const recheckClientWall = async (): Promise<void> => {
    if (search.trim()) void loadClients(search.trim())
    const id = selectedClient?.id ?? (restrictedClient ? restrictedClientId.current : null)
    if (!id) return
    try {
      const client = await getClientByIdApi(id)
      if (restrictedClient) { setRestrictedClient(false); await handleSelectClient(client) } else void refreshClientCasesRef.current?.()
    } catch (err) {
      if (!isRestrictedClientError(err) || !selectedClient) return
      setSelectedClient(null); showRestrictedClient(id); setClientSearchMode('search')
    }
  }
  const currentActivityFilters = (): { actionType?: string; caseId?: string; correspondentType?: string } => ({
    actionType: activityTypeFilter === 'all' ? undefined : activityTypeFilter,
    caseId: activityCaseFilter || undefined,
    correspondentType: activityCorrespondentFilter === null ? undefined : activityCorrespondentFilter,
  })

  // ── Stage 1 live-freshness: another firm user created an activity ────
  // When a DIFFERENT user logs an activity for the client this view has open,
  // refresh the timeline so it appears without a manual reload. A REFETCH
  // (not a raw prepend) because the feed is server-FILTERED (type/case/
  // correspondent) AND renders a server-computed TOTAL footer — a blind
  // prepend from the event summary would show a row the active filter should
  // exclude and leave the £/units total stale. The refetch reuses the exact
  // path create/edit/delete already use (loadActivitiesForClient with the
  // CURRENT filters + the correspondent-chip source), so filters + total stay
  // correct. Echo is suppressed upstream in the presence manager (the actor
  // already sees its own create via the create flow), so only OTHER users'
  // creates land here. Debounced so a burst (e.g. a rapid sequence / import)
  // coalesces into one refetch. The live filters are read from a ref so the
  // mounted-once subscription always refetches with the latest filter state.
  const liveActivityFiltersRef = useRef(currentActivityFilters())
  liveActivityFiltersRef.current = currentActivityFilters()
  const liveActivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    const scheduleRefetch = (): void => {
      if (liveActivityTimerRef.current) clearTimeout(liveActivityTimerRef.current)
      liveActivityTimerRef.current = setTimeout(() => {
        liveActivityTimerRef.current = null
        const cur = useCasevoStore.getState().selectedClient
        if (!cur) return
        void loadActivitiesForClient(cur.id, liveActivityFiltersRef.current)
        void loadCorrespondentChipSource(cur.id)
      }, 400)
    }
    const offs = [
      presence.subscribeEntityChanged((e) => {
        // Stage 1 = create, Stage 2 = delete. Both resolve to the SAME scoped
        // refetch: the timeline is server-filtered + totalled, so re-pulling with
        // the current filters keeps the rows AND the £/units footer correct —
        // a blind row-remove would desync the total. Echo-suppressed upstream.
        if (e.entity !== 'activity' || (e.op !== 'create' && e.op !== 'delete' && e.op !== 'update')) return
        const c = useCasevoStore.getState().selectedClient
        if (!c || e.scope?.clientId !== c.id) return
        scheduleRefetch()
      }),
      // RE-connect: events sent while the socket was down are lost — refetch
      // with the current filters (as the billing screens do, useBillingLive).
      presence.subscribeReconnected(() => {
        if (useCasevoStore.getState().selectedClient) scheduleRefetch()
      }),
    ]
    return () => {
      offs.forEach((off) => off())
      if (liveActivityTimerRef.current) clearTimeout(liveActivityTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ★ Tasks tab live-sync — OTHER users' task changes land in this client's
  // list (own changes are echo-suppressed upstream and already applied by the
  // shared quickUpdateTask engine). The client list is local state, so the
  // global useEntityChangeSync (which patches store.tasks for My Work) doesn't
  // reach it; this mirrors it for the client's slice:
  //   update → patch the row from the event summary (+ drop it if access was
  //            revoked or it moved off this client); not listed → fetch + insert
  //            if it now belongs (e.g. re-linked to one of the client's cases)
  //   create → fetch the row, insert if it belongs to this client
  //   delete → remove the row
  // "Belongs" = the SAME rule as the initial load: clientId is this client, or
  // caseId is one of this client's cases.
  const clientTasksRef = useRef<Task[] | null>(null)
  clientTasksRef.current = clientTasks
  const liveTaskScopeRef = useRef<{ clientId: string | null; caseIds: Set<string> }>({ clientId: null, caseIds: new Set() })
  liveTaskScopeRef.current = { clientId: selectedClient?.id ?? null, caseIds: new Set((clientCases ?? []).map((c) => c.id)) }
  useEffect(() => {
    const belongs = (t: Pick<Task, 'clientId' | 'caseId'>): boolean => {
      const sc = liveTaskScopeRef.current
      return !!sc.clientId && (t.clientId === sc.clientId || (t.caseId !== null && sc.caseIds.has(t.caseId)))
    }
    const inFlight = new Set<string>()
    const fetchInsert = (id: string): void => {
      if (inFlight.has(id)) return
      inFlight.add(id)
      const clientAtStart = liveTaskScopeRef.current.clientId
      void getTaskByIdApi(id)
        .then((fresh) => {
          if (liveTaskScopeRef.current.clientId !== clientAtStart || !belongs(fresh)) return
          setClientTasks((rows) => {
            if (!rows) return rows
            const dupe = rows.find((t) => t.id === fresh.id)
            if (!dupe) return [...rows, fresh]
            return new Date(fresh.updatedAt).getTime() > new Date(dupe.updatedAt).getTime() ? rows.map((t) => (t.id === fresh.id ? fresh : t)) : rows
          })
        })
        .catch(() => { /* no access / gone — nothing to insert */ })
        .finally(() => { inFlight.delete(id) })
    }
    return presence.subscribeEntityChanged((e) => {
      if (e.entity !== 'task' || !liveTaskScopeRef.current.clientId) return
      if (e.op === 'delete' || e.revoked) {
        setClientTasks((rows) => (rows && rows.some((t) => t.id === e.id) ? rows.filter((t) => t.id !== e.id) : rows))
        return
      }
      if (e.op === 'create') { fetchInsert(e.id); return }
      if (e.op !== 'update') return
      // Read membership from the live ref (a functional updater may run later
      // under automatic batching, so it can't report back synchronously).
      if (!(clientTasksRef.current ?? []).some((t) => t.id === e.id)) { fetchInsert(e.id); return }
      setClientTasks((rows) => {
        if (!rows) return rows
        const row = rows.find((t) => t.id === e.id)
        if (!row) return rows
        const merged = { ...row, ...((e.summary ?? {}) as Partial<Task>), ...(e.at ? { updatedAt: e.at } : {}) }
        return belongs(merged) ? rows.map((t) => (t.id === e.id ? merged : t)) : rows.filter((t) => t.id !== e.id)
      })
    })
  }, [])
  // ★ (audit) Cases tab live-sync — a colleague creating / renaming / closing /
  // deleting one of THIS client's cases refreshes the client's case list (the
  // Cases tab, its count, and the Notes/Activity/Tasks case labels all derive
  // from it). Events are audience-filtered server-side; we only re-pull the
  // same audience-scoped list (no event content is rendered). Debounced.
  const refreshClientCasesRef = useRef<() => Promise<void>>(async () => {})
  const clientCaseIdsRef = useRef<Set<string>>(new Set())
  clientCaseIdsRef.current = new Set((clientCases ?? []).map((c) => c.id))
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null
    const unsub = presence.subscribeEntityChanged((e) => {
      if (e.entity !== 'case') return
      const cid = useCasevoStore.getState().selectedClient?.id
      if (!cid) return
      if (e.scope?.clientId !== cid && !clientCaseIdsRef.current.has(e.id)) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => { timer = null; void refreshClientCasesRef.current() }, 400)
    })
    return () => { unsub(); if (timer) clearTimeout(timer) }
  }, [])
  // (D12) Cases changed firm-wide (incl. the actor's other tabs) → the open
  // client's Cases tab re-reads.
  useFirmChange(['cases'], () => { if (useCasevoStore.getState().selectedClient) void refreshClientCasesRef.current() })
  // People chips on the task rows read the shared firmUsers slice (load-once).
  useEffect(() => {
    // (Batch 4) the Cases tab's fee-earner chip resolves owner ids the same way.
    if (activeClientSection === 'tasks' || activeClientSection === 'cases') void useCasevoStore.getState().loadFirmUsers()
  }, [activeClientSection])

  // Stage E — load this client's email drafts whenever the Activity
  // section is shown (that's where the Drafts list + Compose live).
  // Cleared when no client is selected so a stale list can't leak
  // across a client switch.
  useEffect(() => {
    if (activeClientSection !== 'activity' || !selectedClient) {
      setClientDrafts([])
      setDraftsClientId(null)
      return
    }
    void loadDraftsForClient(selectedClient.id)
  }, [activeClientSection, selectedClient, loadDraftsForClient])

  // Action-type filter change handler. Sets the local filter then
  // refetches server-side with the chosen type ('all' → no actionType
  // param). Server-side (not client-side over clientActivities) so the
  // footer total tracks the filter — see the state's docblock.
  const handleActivityTypeFilterChange = (next: ActivityTypeFilter): void => {
    setActivityTypeFilter(next)
    if (!selectedClient) return
    // Flag the in-flight refetch so the list renders a loading
    // treatment (dim + "Updating…") instead of letting the stale rows
    // pose as the filtered result. Cleared in .finally so BOTH paths
    // unset it: loadActivitiesForClient swallows failures into
    // activityError and always resolves, so .finally runs on success
    // and error alike (try/finally semantics). Threads BOTH filters:
    // the NEW actionType + the CURRENT case filter (so changing type
    // keeps the active case).
    setActivitiesRefetching(true)
    void loadActivitiesForClient(selectedClient.id, {
      actionType: next === 'all' ? undefined : next,
      caseId: activityCaseFilter || undefined,
      correspondentType: activityCorrespondentFilter === null ? undefined : activityCorrespondentFilter,
    }).finally(() => setActivitiesRefetching(false))
  }
  // CASE filter change — mirror of the type handler. Threads the NEW
  // caseId + the CURRENT actionType + CURRENT correspondent (so changing
  // case keeps the other two). '' → no caseId (all cases). Same
  // dim/"Updating…" treatment.
  const handleActivityCaseFilterChange = (nextCaseId: string): void => {
    setActivityCaseFilter(nextCaseId)
    if (!selectedClient) return
    setActivitiesRefetching(true)
    void loadActivitiesForClient(selectedClient.id, {
      actionType: activityTypeFilter === 'all' ? undefined : activityTypeFilter,
      caseId: nextCaseId || undefined,
      correspondentType: activityCorrespondentFilter === null ? undefined : activityCorrespondentFilter,
    }).finally(() => setActivitiesRefetching(false))
  }
  // CORRESPONDENT filter change — threads the NEW correspondent + the
  // CURRENT actionType + case. `next` is null (All), '' (None/untagged),
  // or a type string. Same server-side refetch + dim treatment.
  const handleActivityCorrespondentFilterChange = (next: string | null): void => {
    setActivityCorrespondentFilter(next)
    if (!selectedClient) return
    setActivitiesRefetching(true)
    void loadActivitiesForClient(selectedClient.id, {
      actionType: activityTypeFilter === 'all' ? undefined : activityTypeFilter,
      caseId: activityCaseFilter || undefined,
      correspondentType: next === null ? undefined : next,
    }).finally(() => setActivitiesRefetching(false))
  }
  // Clear ALL THREE activity filters back to their defaults and refetch
  // once, unfiltered. Presentation affordance only — reuses the existing
  // state setters + loadActivitiesForClient (no new fetch logic) and the
  // same dim/"Updating…" treatment as the individual filter changes.
  const handleClearActivityFilters = (): void => {
    setActivityTypeFilter('all')
    setActivityCaseFilter('')
    setActivityCorrespondentFilter(null)
    if (!selectedClient) return
    setActivitiesRefetching(true)
    void loadActivitiesForClient(selectedClient.id).finally(() => setActivitiesRefetching(false))
  }

  // Confirm handler for the delete-activity ConfirmDialogue. Fires
  // ONLY after the dialogue's "Delete" button is clicked. On success:
  // close the dialogue + refetch (so the row disappears + the header
  // total decrements). On failure: leave the row in place, surface the
  // backend message in the section-level activityError banner.
  // Mirrors handleConfirmDelete (the document-delete flow) verbatim.
  const handleConfirmDeleteActivity = async (): Promise<void> => {
    if (!confirmDeleteActivity || !selectedClient) return
    setDeletingActivityId(confirmDeleteActivity.id)
    try {
      await deleteActivityApi(confirmDeleteActivity.id)
      setConfirmDeleteActivity(null)
      // Preserve ALL THREE active filters (type + case + correspondent)
      // on refetch so the row disappears without dropping any filter.
      await loadActivitiesForClient(selectedClient.id, currentActivityFilters())
      // Data changed → refresh the correspondent-chip source (a deleted
      // row may have been the last of its correspondent type).
      void loadCorrespondentChipSource(selectedClient.id)
    } catch (err) {
      // Closed first: the reason shows on the list, not behind the dialog.
      setConfirmDeleteActivity(null)
      const message = err instanceof Error ? err.message : 'Failed to delete activity'
      setActivityError(message)
    } finally {
      setDeletingActivityId(null)
    }
  }

  const handleSelectClient = async (client: Client): Promise<void> => {
    // Stage 3d: this function is now CAPTURE-FREE. Stage 3b previously
    // pushed a viewHistory descriptor here, but handleSelectClient is
    // called from THREE call sites and only ONE is a real navigation:
    //   1. Result-row onClick (~L1089) — genuine user click; the
    //      capture now lives at that call site, alongside the click
    //      that constitutes the search→detail nav.
    //   2. pendingClientId consumer effect (~L792) — programmatic;
    //      the calling view (CalendarView.handleOpenClient) already
    //      pushed { view: 'calendar' } at its own funnel-bypass site
    //      per Stage 3c. Adding a push here would double-capture the
    //      one nav (the "must press Back ~5 times" bug).
    //   3. Remount-refetch effect (~L811) — programmatic; fires on
    //      ClientSearchView remount when selectedClient survived from
    //      a prior visit (typical after Case Detail → in-app ← Back).
    //      It's a re-hydration of local section state, not a nav.
    // ★ Latest-selection guard: every async result below is dropped if the
    // user has since switched to another client (fast A→B via favourites /
    // deep link / Back), so A's data can never render under B.
    // (audit) Switching to a DIFFERENT client: persist the outgoing client's
    // pending Client-details edit first — the autosave re-baselines on the
    // new client, which would otherwise strand (and later overwrite) it.
    const prevId = useCasevoStore.getState().selectedClient?.id
    if (prevId && prevId !== client.id) {
      await Promise.all([flushClientNotesAutoSave(), flushClientAutoSave()])
      setNewCaseError(null)
      setChipQuickView(null)
      setResumeDraft(null)
      // A bill opened on the previous client's Activity tab isn't this one's.
      setClientBill(null)
      setRaiseFor(null)
    }
    activeClientIdRef.current = client.id
    const isStale = (): boolean => activeClientIdRef.current !== client.id
    setSelectedClient(client)
    setClientSearchMode('detail')
    setClientCases(null)
    setRestrictedClientCaseIds([])
    setCasesError(null)
    setClientTasks(null)
    setTasksError(null)
    setClientActivities(null)
    setClientActivitiesTotal({ amountPence: 0, durationSeconds: 0, count: 0 })
    setActivityError(null)
    // Reset all three activity filters + the chip source on client
    // switch — the initial load below is UNFILTERED, so the filter UI
    // must start from its unfiltered defaults to stay consistent.
    // Exception: the case quick-view's "View all activity" asks for this
    // client's feed PRE-FILTERED to one case (one-shot, matching client only).
    // Cleared once the activity load settles — not here — because the remount
    // and pendingClientId effects can both open the same client in one commit.
    const pendingActivityCase = useCasevoStore.getState().pendingClientActivityCase
    const presetCaseId = pendingActivityCase?.clientId === client.id ? pendingActivityCase.caseId : ''
    setActivityTypeFilter('all')
    setActivityCaseFilter(presetCaseId)
    setActivityCorrespondentFilter(null)
    setClientActivitiesAll(null)
    setActivitiesRefetching(false)
    setClientDocuments(null)
    setDocumentsError(null)
    setActionError(null)
    // 1. Cases first — needed for the tasks filter.
    let cases: ClientCaseRow[] = []
    try {
      const raw = await getCasesForClientApi(client.id)
      if (isStale()) return
      cases = splitClientCaseRows(raw)
      setClientCases(cases)
    } catch (err) {
      if (isStale()) return
      const message = err instanceof Error ? err.message : 'Failed to load cases for this client'
      setCasesError(message)
      setClientCases([])
    }
    const clientCaseIds = new Set(cases.map((c) => c.id))
    // 2. Tasks + documents in parallel. allSettled so one failure
    // doesn't block the other section from rendering.
    await Promise.allSettled([
      listTasksApi()
        .then((all) => {
          if (isStale()) return
          const filtered = all.filter(
            (t) => t.clientId === client.id || (t.caseId !== null && clientCaseIds.has(t.caseId)),
          )
          setClientTasks(filtered)
        })
        .catch((err) => {
          if (isStale()) return
          const message = err instanceof Error ? err.message : 'Failed to load tasks for this client'
          setTasksError(message)
          setClientTasks([])
        }),
      // Activity — backend supports { clientId } filter directly, so no
      // client-side narrowing (unlike tasks, which needs the case-id set
      // to catch case-linked-only entries). Response bundles the total
      // sibling so we grab both in one call.
      listActivitiesApi({ clientId: client.id, caseId: presetCaseId || undefined })
        .then(({ activities, total }) => {
          if (isStale()) return
          setClientActivities(activities)
          setClientActivitiesTotal(total)
          // An UNFILTERED initial fetch is also the correct source for the
          // contextual correspondent chips; a case-preset one isn't, so the
          // chip source is loaded separately (as after a mutation).
          if (presetCaseId) void loadCorrespondentChipSource(client.id)
          else setClientActivitiesAll(activities)
        })
        .catch((err) => {
          if (isStale()) return
          const message = err instanceof Error ? err.message : 'Failed to load activity for this client'
          setActivityError(message)
          setClientActivities([])
          setClientActivitiesTotal({ amountPence: 0, durationSeconds: 0, count: 0 })
          setClientActivitiesAll([])
        })
        .finally(() => {
          if (presetCaseId && useCasevoStore.getState().pendingClientActivityCase === pendingActivityCase) {
            useCasevoStore.getState().setPendingClientActivityCase(null)
          }
        }),
      loadDocumentsForClient(client.id),
    ])
  }

  // Cross-view "open this client in detail" trigger. CalendarView
  // sets pendingClientId + clientSearchMode='detail' + showClientSearch=true
  // to deep-link a task's linked-client into our detail view.
  //
  // Resolution has THREE tiers (cheapest first, matching Stage 3's
  // restoreView clientDetail lookup):
  //   1. Found in clients[] cache → call handleSelectClient synchronously.
  //   2. Not in clients[] (empty array OR array present but doesn't
  //      contain the target) → fetch by id via getClientByIdApi and then
  //      call handleSelectClient with the fetched object.
  //   3. Fetch fails (deleted / 403 / 404 / network) → fall back to
  //      search mode so the user sees the search list rather than a
  //      blank detail pane. Mirrors restoreView's clientDetail failure
  //      fallback at useCasevoStore.ts L787.
  //
  // Pre-fix, tier 2 didn't exist — the effect early-returned when
  // clients.length === 0, leaving selectedClient null and the L1119
  // detail gate rendering nothing. The Calendar → Open-client deep-link
  // never populates clients[] itself (no on-mount loadClients here),
  // so before this fix the deep-link showed a blank pane whenever
  // the user hadn't previously triggered a clients load elsewhere in
  // the session (TasksView / DocumentSearchView / ClientPickerModal /
  // typed search).
  //
  // Concurrency discipline: cache pendingClientId locally and clear
  // the store slot IMMEDIATELY (before any async work). That way, if
  // clients[] populates via a concurrent search-debounce mid-fetch and
  // this effect re-fires on the clients dep change, the pendingClientId
  // dep is now null → early return → no double consume. The effect
  // callback itself stays SYNC (React's rule); async work lives inside
  // a void-returning IIFE.
  useEffect(() => {
    if (!pendingClientId) return
    const targetId = pendingClientId
    // Clear up-front — see concurrency note above.
    setPendingClientId(null)
    // (audit) Re-opening the client that's ALREADY selected (e.g. a section
    // favourite for the open client): the id-keyed landing effect won't re-run,
    // so apply + consume the pending section here (it used to leak to the next
    // client opened).
    if (targetId === useCasevoStore.getState().selectedClient?.id) {
      const sec = useCasevoStore.getState().pendingClientSection
      if (sec) {
        useCasevoStore.getState().setPendingClientSection(null)
        if (Object.prototype.hasOwnProperty.call(CLIENT_SECTION_LABELS, sec)) {
          setActiveClientSection(sec as keyof typeof CLIENT_SECTION_LABELS)
          useCasevoStore.getState().setClientSectionMemo({ clientId: targetId, section: sec })
        }
      }
    }
    // Tier 1: cache hit.
    const cached = clients.find((c) => c.id === targetId)
    // (Audit batch 3, H4) A walled client's search stub never opens.
    if (cached?.restricted) { showRestrictedClient(targetId); setClientSearchMode('search'); return }
    if (cached) {
      void handleSelectClient(cached)
      return
    }
    // Tier 2/3: fetch by id.
    void (async () => {
      try {
        const client = await getClientByIdApi(targetId)
        await handleSelectClient(client)
      } catch (err) {
        // (Audit batch 3, H4) Behind an ethical wall → the "Restricted client" page.
        if (isRestrictedClientError(err)) { showRestrictedClient(targetId); setClientSearchMode('search'); return }
        // Tier 3 — client gone or unreachable. Drop the user into
        // search mode; do NOT leave a blank detail pane.
        // eslint-disable-next-line no-console
        console.error('[ClientSearchView] pendingClientId fetch failed for', targetId, err)
        setClientSearchMode('search')
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingClientId, clients, setPendingClientId])

  // Remount-refetch effect. selectedClient lives in the store and
  // survives an unmount; clientCases / clientTasks / clientDocuments
  // are local state and don't. After a Case Detail → ← Back round
  // trip, ClientSearchView remounts with selectedClient already set
  // but the section data null — without this effect the sections
  // would render "Loading..." indefinitely (no fetch in flight).
  // Empty deps = mount-only; handleSelectClient is idempotent when
  // called with the already-selected client (re-sets the same value,
  // resets section state to null, re-fetches).
  useEffect(() => {
    // (audit) skip when a DIFFERENT client is pending — the pendingClientId
    // effect opens it; re-selecting the stale one here raced it.
    const pendingId = useCasevoStore.getState().pendingClientId
    if (pendingId && pendingId !== selectedClient?.id) return
    if (selectedClient && clientCases === null && view === 'detail') {
      void handleSelectClient(selectedClient)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Stage 4b: handleBackFromSearch / handleBackFromDetail / the
  // handleBack ternary were removed. The leftslot Back button now
  // delegates to window.history.back(), which pops the viewHistory
  // trail via App.tsx's popstate handler (which also runs the 5-way
  // autosave flush installed in Stage 4a). Both browser Back and
  // in-app Back therefore share exactly one restore pathway.
  //
  // "Go home" (wordmark) stays bespoke — the wordmark is not "step
  // back"; it deliberately closes the entire takeover regardless of
  // sub-mode. Awaits both client auto-save flushes so a pending
  // Client edit (Stage 2a) persists before the takeover unmounts.
  // The Notes flush is awaited for the same client-lifecycle reason
  // as the (now-removed) detail-mode back handler: useClientAutoSave's
  // selector watches selectedClient; if we closed the takeover with
  // an in-flight payload, the effect cleanup would drop pendingPayload
  // silently.
  const handleHome = async (): Promise<void> => {
    await Promise.all([flushClientNotesAutoSave(), flushClientAutoSave()])
    await navigateTo({ view: 'caseList' })
  }

  // Textarea onChange — update local notesValue (which is the
  // controlled-input source of truth) and schedule a debounced save.
  // Note: notesValue is local NOT bound to selectedClient.notes,
  // because the store's value lags by one save round-trip and would
  // overwrite the user's in-progress typing. After each successful
  // save the store refreshes, but the textarea keeps showing the
  // local value.
  // Client-detail nav-click intercept. When the user navigates AWAY
  // from the Notes section (i.e. activeClientSection was 'notes' and
  // the next click goes elsewhere), the Notes textarea is about to
  // unmount. Any pending debounced notes save would still drain via
  // the module-scope singleton, but firing flushClientNotesAutoSave
  // here makes the save fire IMMEDIATELY rather than at the next
  // 1 s debounce tick — so a quick "type → click another section"
  // sequence never loses the last keystroke. Fire-and-forget (void)
  // because the click is synchronous; the singleton's runSave will
  // complete in the background regardless of when this component
  // re-renders. The existing flushes inside the nav-away handlers
  // (handleHome / handleOpenTask / handleNewTaskForClient) plus the
  // Stage-4a popstate flush (App.tsx) cover the "leaving the client
  // entirely" path; this one is specifically for "staying on the
  // client but leaving the Notes section."
  const handleClientNavClick = (next: typeof activeClientSection): void => {
    if (next === activeClientSection && !clientBill) return
    if (activeClientSection === 'notes' && next !== 'notes') {
      void flushClientNotesAutoSave()
    }
    // A tab is a place of its own: Back returns to the tab you were on.
    void navigateWithin(() => {
      setActiveClientSection(next)
      // A bill opened on the Activity tab belongs to it — switching closes it.
      setClientBill(null)
      setRaiseFor(null)
      // Remember it so closing a task / Back from a case returns to THIS tab.
      if (selectedClient) useCasevoStore.getState().setClientSectionMemo({ clientId: selectedClient.id, section: next })
    })
  }
  // Nav sub-line counts (same slot Admin uses for "3 active"); only once loaded.
  const clientNavSub = (key: typeof activeClientSection): string | null => {
    // (Batch 4) incl. locked cases — matches the Cases tab's "All" count.
    if (key === 'cases') { const n = (clientCases?.length ?? 0) + restrictedClientCaseIds.length; return clientCases && n ? String(n) : null }
    if (key === 'tasks') return clientTasks && clientTasks.length ? String(clientTasks.length) : null
    return null
  }

  // Open a case from the detail view. Flushes any in-flight auto-save
  // (safe no-ops here since we're not on a case-detail screen), sets
  // currentCase via the store, then closes the Client Search takeover
  // so App.tsx renders the case detail.
  // ★ Client-card case chip → the EXISTING case quick-view modal (the same
  // CaseQuickViewModal the case list opens). The chip only carries id/ref/
  // status, so fetch the case (getCaseById — access-checked server-side), narrow
  // it with the shared rawToSummary, and seed the modal with the full row so it
  // paints immediately. A 403/404 surfaces the shared clean message.
  const [chipQuickView, setChipQuickView] = useState<{ summary: CaseSummary; full: RawCase } | null>(null)
  const [openingCaseId, setOpeningCaseId] = useState<string | null>(null)
  const openCaseQuickView = (caseId: string): void => {
    if (openingCaseId) return
    setOpeningCaseId(caseId)
    void fetchCaseById(caseId)
      .then((raw) => setChipQuickView({ summary: rawToSummary(raw), full: raw }))
      .catch((err) => {
        const msg = caseOpenErrorMessage(err)
        useCasevoStore.getState().setSaveConflict(msg ?? 'Failed to open case. Please try again.')
      })
      .finally(() => setOpeningCaseId(null))
  }

  // `prefetched` (optional) = the row the case quick-view already fetched, so
  // "Full case →" from a client-card chip skips the getCaseById round-trip.
  const handleOpenCase = async (caseId: string, prefetched?: RawCase): Promise<void> => {
    try {
      // Back from the case returns to this client on this tab (the history
      // entry carries the tab). keepUntilShown: leave the client page the
      // moment the case row is mapped (witnesses/deadlines/TPI/files fill in
      // behind it) — not after every satellite load.
      await navigateTo({ view: 'caseDetail', caseId }, { prefetched, keepUntilShown: true, intents: (s) => s.setCaseOpenedFrom('clientSearch') })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ClientSearchView] Failed to open case:', err)
    }
  }

  // "New invoice": load the case's billing (RaiseBillModal needs it), then open
  // Create bill over this page. One case → straight in; several → pick first.
  const startRaiseBill = (caseId: string): void => {
    if (raisingCaseId) return
    setRaisingCaseId(caseId)
    setActionError(null)
    const opt = activityCaseOptions.find((c) => c.id === caseId)
    void getCaseBillingApi(caseId)
      .then((data) => setRaiseFor({ caseId, name: data.case?.name ?? opt?.name ?? '', ref: data.case?.ref ?? opt?.ref ?? '', data }))
      .catch((e) => setActionError(errorText(e, 'Couldn’t load the case’s billing.')))
      .finally(() => setRaisingCaseId(null))
  }
  const handleNewInvoice = (): void => {
    if (clientCases !== null && activityCaseOptions.length === 1) startRaiseBill(activityCaseOptions[0].id)
    else setPickBillCase(true)
  }
  // The draft is created → open it here as its own history step, so Back (in-app
  // or browser) returns to this client's Activity tab.
  const openClientBill = (bill: Bill): void => {
    setRaiseFor(null)
    void navigateWithin(() => setClientBill({ id: bill.id, initial: bill }))
  }
  // ← Activity: the place you were on (usePlaceParam closes the bill + reloads
  // the feed); opened fresh from a link → just close it.
  const closeClientBill = (): void => {
    goBack(() => { setClientBill(null); reloadBillEvents() })
  }

  // Callum #1: cases the client's cases are LINKED TO. Aggregate every
  // client case's caseLinks, DEDUPE by caseId (the same linked case can
  // appear via several of the client's cases), and EXCLUDE any that are
  // the client's OWN cases (a link between two of this client's cases is
  // already in the list above). Empty → the "Linked to" section is hidden.
  // Each keeps fromCaseId — the client's case the link was read from — so the
  // relationship badge saves against that pair. When one linked case is
  // linked from several of the client's cases, an UNCONFIRMED pair wins, so
  // "Please confirm" is never hidden behind a confirmed one.
  const linkedToCases = useMemo<(CaseLinkSummary & { fromCaseId: string })[]>(() => {
    if (!clientCases) return []
    const ownIds = new Set(clientCases.map((c) => c.id))
    const byId = new Map<string, CaseLinkSummary & { fromCaseId: string }>()
    for (const c of clientCases) {
      for (const l of c.caseLinks ?? []) {
        if (ownIds.has(l.caseId)) continue
        const prev = byId.get(l.caseId)
        if (!prev || (prev.relationshipConfirmed !== false && l.relationshipConfirmed === false)) byId.set(l.caseId, { ...l, fromCaseId: c.id })
      }
    }
    return Array.from(byId.values())
  }, [clientCases])
  // A relationship set anywhere in this tab (here, or on the case page) →
  // patch this client's case rows at once. Other users' changes arrive via
  // the 'case' entity-changed refetch above.
  useEffect(() => subscribeLinkRelationship((change) => {
    setClientCases((rows) => {
      if (!rows) return rows
      let hit = false
      const next = rows.map((c) => {
        if (!c.caseLinks) return c
        const links = patchLinks(c.caseLinks, c.id, change)
        if (links === c.caseLinks) return c
        hit = true
        return { ...c, caseLinks: links }
      })
      return hit ? next : rows
    })
  }), [])
  // This client's cases + THE reference each, for the pickers in the Add-activity /
  // Upload-document modals (so they label cases exactly like everywhere else).
  const clientCasesWithRef = useMemo(
    () => (clientCases ?? []).map((c) => ({ ...c, ref: caseRef({ ...c, client: selectedClient }), name: caseName({ ...c, client: selectedClient }) })),
    [clientCases, selectedClient],
  )
  // Link-case picker + chip labels for the Notes feed. getCasesForClient is
  // audience-filtered, so these are exactly the cases the viewer may link.
  // Activity feed case chips + case filter (name + THE reference).
  const activityCaseOptions = useMemo<ActivityCaseOption[]>(
    () => clientCasesWithRef.map((c) => ({ id: c.id, name: caseName({ ...c, client: selectedClient }), ref: c.ref })),
    [clientCasesWithRef, selectedClient],
  )
  const noteCaseOptions = useMemo<NoteCaseOption[] | null>(
    () => (clientCases === null ? null : clientCasesWithRef.map((c) => ({
      id: c.id,
      label: caseName({ ...c, client: selectedClient }),
      ref: c.ref,
      closed: !!c.closeDate,
    }))),
    [clientCases, clientCasesWithRef, selectedClient],
  )
  // Access state of each linked case — the SAME batched check + locked-link
  // treatment the case page's Linked cases section uses (useCaseAccess).
  const linkedAccessOf = useCaseAccess(linkedToCases.map((l) => l.caseId))

  // ── Cases tab (ClientCasesTab, mockup Option B) — rows → card models via
  // the SHARED caseName()/caseRef()/getStatus/calculateCaseCompletion, so a
  // card reads exactly like the caselist + quick view.
  const firmUsers = useCasevoStore((s) => s.firmUsers)
  const caseCards = useMemo<CaseCardModel[] | null>(() => {
    if (clientCases === null) return null
    const byId = new Map(firmUsers.map((u) => [u.id, u]))
    const open: CaseCardModel[] = clientCases.map((c) => {
      // (Batch 4) Fee-earner — the case owner resolved through the shared
      // firmUsers slice (name + their avatar colour). Unassigned → null
      // ("Unassigned" chip); owner not loaded yet → omitted (no chip flash).
      const ownerId = c.assignedToUserId ?? null
      const owner = ownerId ? byId.get(ownerId) : undefined
      const assignee = ownerId === null
        ? null
        : owner
          ? { name: `${owner.firstName ?? ''} ${owner.lastName ?? ''}`.trim() || owner.email || 'Former member', colour: owner.avatarColour || '#2d6a4f' }
          : undefined
      return {
        id: c.id,
        name: caseName({ ...c, client: selectedClient }),
        ref: caseRef({ ...c, client: selectedClient }),
        status: getStatus(c).label as CaseStatusLabel,
        accidentDate: c.accidentDate ? formatDateUK(c.accidentDate) : null,
        completion: calculateCaseCompletion(c),
        favLabel: caseNameWithRef({ ...c, client: selectedClient }),
        assignee,
        // "You" chip + gold bar — the viewer is the fee-earner.
        mine: !!authUser && ownerId === authUser.id,
        // Delete: the assignee, an admin, or "Delete cases" (caseController.deleteCase).
        canDelete: canDeleteCases(authUser, [{ assignedToUserId: ownerId }]),
        sameAccidentCount: c.accidentGroupSize ?? 0,
      }
    })
    // (Batch 4) The client's cases this viewer can't open → locked cards.
    const locked: CaseCardModel[] = restrictedClientCaseIds.map((id) => ({
      id, name: '', ref: '', status: 'Open', accidentDate: null, completion: null, locked: true,
    }))
    return [...open, ...locked]
  }, [clientCases, restrictedClientCaseIds, selectedClient, authUser, firmUsers])
  const linkedCards = useMemo<LinkedCaseModel[]>(() => linkedToCases.map((l) => ({
    id: l.caseId,
    name: caseName(l),
    ref: caseRef({ ...l, id: l.caseId }),
    clientName: caseClientName(l) || 'Unnamed client',
    locked: linkedAccessOf(l.caseId) === 'locked',
    fromCaseId: l.fromCaseId,
    relationship: l.relationship,
    relationshipConfirmed: l.relationshipConfirmed,
  })), [linkedToCases, linkedAccessOf])
  // ── Case actions on the Cases-tab cards — the SAME shared CaseActionsMenu the
  // sidebar uses (CaseContextMenu + every dialog: rename / delete / link /
  // assign / duplicate / export / status), so behaviour, permissions (403 →
  // server reason) and the favourites cache are identical. The row is upgraded
  // to the full case in the background (like the sidebar's favourite rows).
  const [caseMenu, setCaseMenu] = useState<CaseActionsMenuState | null>(null)
  const [caseDeleteReq, setCaseDeleteReq] = useState<CaseSummary | null>(null)
  const clientRowSummary = (id: string): CaseSummary | null => {
    const row = clientCases?.find((x) => x.id === id)
    if (!row) return null
    return {
      clientDateOfBirth: null, clientNiNumber: '', clientMainContactNumber: '', clientEmailAddress: '',
      accidentLocation: '', claimantReg: '', openDate: null, assignedToUserId: null,
      ...row,
      clientForename: row.clientForename ?? selectedClient?.forename ?? '',
      clientSurname: row.clientSurname ?? selectedClient?.surname ?? '',
      client: selectedClient,
    } as unknown as CaseSummary
  }
  const openCaseMenu = (id: string, x: number, y: number): void => {
    const target = clientRowSummary(id)
    if (!target) return
    setCaseMenu({ x, y, target })
    void fetchCaseById(id)
      .then((full) => setCaseMenu((cur) => (cur && cur.target.id === id ? { ...cur, target: full as unknown as CaseSummary } : cur)))
      .catch(() => { /* inaccessible / gone — keep the row-built target */ })
  }
  const refreshClientCases = async (): Promise<void> => {
    const cid = selectedClient?.id
    if (!cid) return
    try {
      const raw = await getCasesForClientApi(cid)
      if (activeClientIdRef.current && activeClientIdRef.current !== cid) return
      setClientCases(splitClientCaseRows(raw))
    } catch { /* keep the current list; the next load retries */ }
  }
  refreshClientCasesRef.current = refreshClientCases

  // "New case for <client>" — the caselist's create path (createCaseApi)
  // minus the client picker (the client is known), then this screen's own
  // open-case path. B2: a small modal asks for the REQUIRED claim type first
  // (NewCaseForClientModal); a refusal of it shows under the field.
  const [creatingCase, setCreatingCase] = useState(false)
  const [newCaseError, setNewCaseError] = useState<string | null>(null)
  const [newCaseOpen, setNewCaseOpen] = useState(false)
  const [newCaseClaimError, setNewCaseClaimError] = useState<string | null>(null)
  const handleNewCaseForClient = async (claim: { claimType: ClaimType; claimRoute: ClaimRoute | null }): Promise<void> => {
    if (!selectedClient || creatingCase) return
    setCreatingCase(true)
    setNewCaseError(null)
    setNewCaseClaimError(null)
    try {
      const created = await createCaseApi({ clientId: selectedClient.id, ...claim })
      setNewCaseOpen(false)
      await handleOpenCase(created.id, created)
    } catch (err) {
      const refused = refusedField(err)
      if (refused?.field === 'claimType' || refused?.field === 'claimRoute') setNewCaseClaimError(refused.message)
      else setNewCaseError('Failed to create case. Please try again.')
    } finally {
      setCreatingCase(false)
    }
  }

  // ── PER-CLIENT TASKS / DOCUMENTS HANDLERS ────────────────────
  // Open an existing task in TasksView. Mirrors handleOpenCase's
  // flush-then-navigate pattern. TasksView's pendingTaskId consumer
  // (L118–129 of TasksView.tsx) reads the slot once tasks are loaded
  // and switches into edit-form mode. We flip showTasks=true and
  // showClientSearch=false so App.tsx's renderMain takes us to
  // TasksView; the precedence order would already pick TasksView
  // since showTasks wins, but setting showClientSearch=false matches
  // the takeover-handoff pattern used elsewhere.
  const handleOpenTask = async (taskId: string): Promise<void> => {
    // Stage 2a: flushClientAutoSave alongside flushClientNotesAutoSave so a
    // pending Client-detail edit persists before we navigate to TasksView.
    await Promise.all([flushClientNotesAutoSave(), flushClientAutoSave()])
    await navigateTo({ view: 'tasks' }, { intents: (s) => { s.setTaskOpenedFrom('clientSearch'); s.setPendingTaskId(taskId) } })
  }
  // "+ New task" for the current client. Sets pendingNewTaskClientId
  // so TasksView opens its create-mode form with this client
  // preselected as the Link to value.
  const handleNewTaskForClient = async (): Promise<void> => {
    if (!selectedClient) return
    // Stage 2a: as in handleOpenTask, flush both Client-edit paths.
    await Promise.all([flushClientNotesAutoSave(), flushClientAutoSave()])
    const clientId = selectedClient.id
    await navigateTo({ view: 'tasks' }, { intents: (s) => { s.setTaskOpenedFrom('clientSearch'); s.setPendingNewTaskClientId(clientId) } })
  }
  // Verbatim mirror of DocumentSearchView.handleDownload — presigned
  // URL, plain fetch (NOT axios — the Bearer interceptor would
  // invalidate the R2 signature), blob via anchor for a true browser
  // Save-As, revoke the object URL on completion.
  // OPEN — preview previewable types (PDF/images/text) inline in a new
  // tab; fall back to a download for everything else. The server is the
  // arbiter of inline (isInlineSafeMime only picks the affordance).
  const handleOpenDocument = async (result: DocumentSearchResult): Promise<void> => {
    setActionError(null)
    setDownloadingId(result.id)
    try {
      // Ask the server for an inline URL. The SERVER is the arbiter: it
      // signs inline only for allowlisted mimes and returns the file's
      // AUTHORITATIVE mimeType. We branch on THAT response mime — never
      // on result.mimeType, which the search endpoint may not populate
      // (a missing row mime was silently forcing the download branch).
      const { downloadUrl, filename, mimeType } = await requestDownloadUrlFlatApi(result.id, true)
      if (isInlineSafeMime(mimeType)) {
        openUrlInTab(downloadUrl)      // server signed inline → renders in-tab
      } else {
        await triggerBlobDownload(downloadUrl, filename)   // server signed attachment
      }
    } catch {
      setActionError('Failed to open file. Please try again.')
    } finally {
      setDownloadingId(null)
    }
  }
  // DOWNLOAD — always downloads (attachment), regardless of type.
  const handleDownloadDocument = async (result: DocumentSearchResult): Promise<void> => {
    setActionError(null)
    setDownloadingId(result.id)
    try {
      const { downloadUrl, filename } = await requestDownloadUrlFlatApi(result.id)
      await triggerBlobDownload(downloadUrl, filename)
    } catch {
      setActionError('Failed to download file. Please try again.')
    } finally {
      setDownloadingId(null)
    }
  }

  // ── DELETE FLOW ──────────────────────────────────────────────
  // Trash-button click opens the confirm dialog. handleConfirmDelete
  // does the actual API call after the user confirms; optimistic
  // local update + refetch-on-failure keeps the UI responsive without
  // leaving the row stuck in a "halfway deleted" state on transient
  // network blips.
  const handleDeleteDocument = (result: DocumentSearchResult): void => {
    setActionError(null)
    setConfirmDeleteDoc(result)
  }
  const handleConfirmDelete = async (): Promise<void> => {
    const doc = confirmDeleteDoc
    if (!doc) return
    setConfirmDeleteDoc(null)
    setDeletingId(doc.id)
    // Optimistic: snapshot the current list, remove the deleted row.
    const previousDocs = clientDocuments
    setClientDocuments((prev) => prev?.filter((d) => d.id !== doc.id) ?? prev)
    try {
      await deleteFileFlatApi(doc.id)
      // Success — optimistic state is correct, nothing more to do.
    } catch (err) {
      // Failure — restore the previous list and surface the error.
      setClientDocuments(previousDocs)
      const message = err instanceof Error ? err.message : 'Failed to delete document. Please try again.'
      setActionError(message)
    } finally {
      setDeletingId(null)
    }
  }
  // ── Tasks tab helpers ──
  // Case name + THE reference for a task's case (same caseName()/caseRef() as
  // the Cases tab), for the My Work row's case link.
  const caseInfoForTask = useCallback((caseId: string | null): CaseInfo | undefined => {
    if (!caseId) return undefined
    const c = clientCases?.find((x) => x.id === caseId)
    return c
      ? { name: caseName({ ...c, client: selectedClient }), ref: caseRef({ ...c, client: selectedClient }) }
      : { name: 'Case', ref: caseShortId(caseId) }
  }, [clientCases, selectedClient])
  // Local-list patcher handed to quickUpdateTask (the store list is patched by
  // the engine itself).
  const patchClientTask = useCallback((id: string, fn: (t: Task) => Task): void => {
    setClientTasks((rows) => (rows && rows.some((t) => t.id === id) ? rows.map((t) => (t.id === id ? fn(t) : t)) : rows))
  }, [])
  // Error-state "Try again": the same load + client/case filter as handleSelectClient.
  const reloadClientTasks = async (): Promise<void> => {
    const client = selectedClient
    if (!client) return
    const caseIds = new Set((clientCases ?? []).map((c) => c.id))
    setTasksError(null)
    setClientTasks(null)
    try {
      const all = await listTasksApi()
      if (activeClientIdRef.current !== client.id) return
      setClientTasks(all.filter((t) => t.clientId === client.id || (t.caseId !== null && caseIds.has(t.caseId))))
    } catch (err) {
      if (activeClientIdRef.current !== client.id) return
      setTasksError(err instanceof Error ? err.message : 'Failed to load tasks for this client')
      setClientTasks([])
    }
  }

  return (
    <div className="min-h-screen md:min-h-full bg-casevo-offwhite font-sans">
      <AppHeader
        onWordmarkClick={handleHome}
        leftHamburger={<SidebarHamburger />}
        leftSlot={<BackButton fallback={view === 'detail' ? { view: 'clientSearch' } : { view: 'dashboard' }} />}
      >
        {/* Stage 2a — auto-save status pill. useClientAutoSave writes
            failures into the shared saveError slot; SaveIndicator's
            error branch reads that slot regardless of context, so a
            failed Client save here surfaces the same red "Couldn't
            save — try again" affordance as on the case page. The
            success-cascade (saving → saved → idle) is gated on
            currentCase.updatedAt and stays idle on this page, matching
            the case-page behaviour for Client (vs Case) saves — both
            views show errors loudly but successes quietly. NOTE: the
            "try again" button currently calls retryAutoSave (case-side
            retry), which is a no-op when there's no case open; the
            user can retry by typing again. A cleaner fix is part of the
            planned 2b/2c untangle. */}
        <SaveIndicator />
      </AppHeader>
      <div className="px-4 py-6 sm:py-8">
        {view === 'search' && restrictedClient && (
          <RestrictedClientPanel onBack={() => setRestrictedClient(false)} />
        )}
        {view === 'search' && !restrictedClient && (
          /* ★ Clients — "People directory" (Option B). Same data + behaviour as
             before: debounced search (min SEARCH_MIN_CHARS), surname A–Z
             (sortedResults), the new-clients banner (re-runs the current
             search), and click-through to the client page (pushViewHistory
             first — the sole user-click entry into detail mode, see the
             Stage 3d note on handleSelectClient). */
          <div className="max-w-[1180px] mx-auto sm:px-[20px] -mt-[6px] sm:-mt-[2px]">
            <ClientDirectory
              search={search}
              onSearch={setSearch}
              inputRef={searchInputRef}
              minChars={SEARCH_MIN_CHARS}
              clients={sortedResults}
              loading={clientsLoading}
              error={clientsError}
              newCount={newClientCount}
              onRefresh={() => { resetNewClients(); void loadClients(search.trim()) }}
              onOpen={(c) => { if (c.restricted) { showRestrictedClient(c.id); return } void navigateWithin(() => { void handleSelectClient(c) }) }}
              onOpenCase={openCaseQuickView}
              openingCaseId={openingCaseId}
            />
          </div>
        )}
        {view === 'detail' && selectedClient && (
          <div className="max-w-[1180px] mx-auto px-4">
            {/* Client-detail header row. Left column keeps the
                existing three-line stack (label + name + client
                number) verbatim; ActionToolbar sits on the right,
                aligned to the vertical centre of that stack on
                sm+. On mobile the flex-wrap lets the toolbar wrap
                under so the h1 has room to breathe. This right-
                edge placement mirrors how "+ Upload document" /
                "+ New activity" sit at the far right of their
                section headers — the toolbar's the page-level
                equivalent of those section-level shortcut
                buttons. */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-widest text-casevo-brand mb-1">Client</p>
                <div className="flex items-center gap-1">
                  <h1 className="text-xl sm:text-2xl font-medium text-casevo-forest">
                    {displayName(selectedClient)}
                  </h1>
                  {/* Favourite star — light-header variant. subSection is
                      the CURRENT section key so Stage 3 can restore it; the
                      label carries a readable section for the sidebar. */}
                  <FavouriteStar
                    itemType="client"
                    itemId={selectedClient.id}
                    label={`${displayName(selectedClient)} · ${CLIENT_SECTION_LABELS[activeClientSection]}`}
                    subSection={activeClientSection}
                    variant="light"
                  />
                </div>
                <p className="text-sm text-casevo-mid mt-1">{selectedClient.clientNumber}</p>
              </div>
              <ActionToolbar
                onSelectVariant={(variantKey) => {
                  // Direct-variant open: bypass the picker and
                  // jump straight into the typed screen. Timer
                  // auto-starts (see AddActivityModal's reset-on-
                  // open effect's `if (variant)` branch).
                  setActivityVariant(variantKey)
                  setShowAddActivityModal(true)
                }}
                onSendEmail={() => setShowComposeEmail(true)}
                emailConnected={msConnected}
              />
            </div>

            {/* ── Section nav — the shared Settings/Admin split-page nav
                (SectionNavItem / SectionNavPill, settingsUi.tsx): raised-card
                active item + primary bar at lg+, scrolling pills below lg.
                Flat list (5 tabs — headings would add noise). Clicks still go
                through handleClientNavClick so the flush-on-leave-notes
                side-effect fires. */}
            <nav aria-label="Client sections" className="lg:hidden -mx-4 px-4 mb-[16px] overflow-x-auto">
              <div className="flex gap-[6px] w-max pb-[2px]">
                {CLIENT_NAV.map((t) => (
                  <SectionNavPill key={t.key} icon={t.icon} label={t.label} active={activeClientSection === t.key} onClick={() => handleClientNavClick(t.key)} />
                ))}
              </div>
            </nav>
            <div className="lg:grid lg:grid-cols-[236px_minmax(0,1fr)] lg:gap-[26px] lg:items-start">
              <nav aria-label="Client sections" className="hidden lg:block sticky top-[20px]">
                <SideScroll from={1024} label="Client sections">
                  {CLIENT_NAV.map((t) => (
                    <SectionNavItem key={t.key} icon={t.icon} label={t.label} active={activeClientSection === t.key} sub={clientNavSub(t.key)} onClick={() => handleClientNavClick(t.key)} />
                  ))}
                </SideScroll>
              </nav>

              {/* ── RIGHT: Conditional section panels ───────────── */}
              <div className="min-w-0">
            {/* CLIENT DETAILS — the redesigned, registry-driven client-info
                sections (components/clientInfo; approved mockup Option C ·
                Hybrid): view cards + per-section edit + Edit all. Inputs write
                straight through updateSelectedClient → useClientAutoSave (the
                same debounced PATCH /api/clients/:id + live-sync/presence the
                old long form used); Save = flushClientAutoSave, Cancel restores
                the captured values. Keyed per client so edit state never leaks
                across clients. The same component renders the case page's
                client details later (prefix 'clientDetails', strict rules). */}
            {activeClientSection === 'details' && (
              <section className="mb-6">
                <ClientInfoSections
                  key={selectedClient.id}
                  person={selectedClient}
                  onChange={(changes) => updateSelectedClient(changes as Partial<Client>)}
                  fieldKeyPrefix="client"
                  onBroadcast={broadcastClientFieldEdits}
                  onFlush={flushClientAutoSave}
                  title="Client details"
                  subtitle={`Personal, contact and medical details for ${selectedClient.forename.trim() || 'this client'} · updated ${new Date(selectedClient.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                  saveHint={`Saves to ${selectedClient.forename.trim() ? `${selectedClient.forename.trim()}’s` : 'the'} client record as you type`}
                >
                  {/* B4c (D4) The mobile e-signature codes go to — saved on Save, audited. Pilot only. */}
                  {/* (E-sign fixes B) + the main phone: a UK mobile there is used for codes (utils/esignMobile) — the same props as the case page. */}
                  <MobileNumberCard key={`mobile:${selectedClient.id}`} {...mobileCardPropsOf(selectedClient)}
                    onSaved={(c) => updateSelectedClient({ mobileNumber: c.mobileNumber ?? null })} />
                </ClientInfoSections>
                {/* B3 — consent to receive bills by email. */}
                <BillEmailConsentCard key={`consent:${selectedClient.id}`} clientId={selectedClient.id} clientName={selectedClient.forename} currentEmail={selectedClient.emailAddress} currentNoEmail={selectedClient.hasEmailAddress === false} />
              </section>
            )}
            {/* NOTES — team note feed (Layout B of the approved notes mockup):
                composer + feed on the left, sticky pinned rail on the right.
                Live backend: /api/clients/:clientId/notes. Keyed per client so
                drafts/editing never leak between clients. The old shared
                notes doc (client.notes) shows read-only in the rail. */}
            {activeClientSection === 'notes' && (
            <section className="mb-6">
              <ClientNotesFeed
                key={selectedClient.id}
                clientId={selectedClient.id}
                clientFirstName={selectedClient.forename ?? ''}
                cases={noteCaseOptions}
                legacyNotesHtml={selectedClient.notes ?? null}
                onOpenCase={openCaseQuickView}
              />
            </section>
            )}
            {/* CASES — real working section. Backend is a placeholder
                today (Case.clientId not yet wired) so this will show
                the empty-state branch until Reuben ships the link. */}
            {/* CASES — compact client-tab cards (ClientCasesTab, approved
                mockup Option B). Every card → the case quick-view (the same
                CaseQuickViewModal the client chips open; "Full case →" opens
                the case). Locked linked cases keep the show-but-lock look.
                "New case for <client>" creates + opens a case for them. */}
            {activeClientSection === 'cases' && (
              <section className="mb-6">
                <ClientCasesTab
                  key={selectedClient.id}
                  clientFirstName={selectedClient.forename}
                  clientFullName={`${selectedClient.forename} ${selectedClient.surname}`}
                  cases={caseCards}
                  error={casesError ?? (newCaseOpen ? null : newCaseError)}
                  linked={linkedCards}
                  onOpen={(id) => openCaseQuickView(id)}
                  openingId={openingCaseId}
                  onNewCase={() => { setNewCaseError(null); setNewCaseClaimError(null); setNewCaseOpen(true) }}
                  creating={creatingCase}
                  onMenu={openCaseMenu}
                  onDelete={(id) => setCaseDeleteReq(clientRowSummary(id))}
                />
                <NewCaseForClientModal
                  isOpen={newCaseOpen}
                  clientName={`${selectedClient.forename} ${selectedClient.surname}`.trim()}
                  busy={creatingCase}
                  claimError={newCaseClaimError}
                  error={newCaseError}
                  onClose={() => setNewCaseOpen(false)}
                  onCreate={(claim) => { void handleNewCaseForClient(claim) }}
                />
                <CaseActionsMenu
                  menu={caseMenu}
                  onClose={() => setCaseMenu(null)}
                  deleteRequest={caseDeleteReq}
                  onDeleteRequestConsumed={() => setCaseDeleteReq(null)}
                  onMutated={(id, kind) => {
                    if (kind === 'deleted') setClientCases((rows) => (rows ? rows.filter((r) => r.id !== id) : rows))
                    void refreshClientCases()
                  }}
                />
              </section>
            )}
            {/* TASKS — ClientTasksTab (approved mockup Option A): the EXACT
                My Work rows (QueueRow) grouped by urgency, status filter with
                counts, add-task row, empty/loading/error states and a task
                actions menu. Actions run the shared quickUpdateTask engine, so a
                task changed here updates My Work too. */}
            {activeClientSection === 'tasks' && (
              <section className="mb-6">
                <ClientTasksTab
                  key={selectedClient.id}
                  clientFirstName={selectedClient.forename}
                  tasks={clientTasks}
                  error={tasksError}
                  onRetry={() => { void reloadClientTasks() }}
                  caseInfo={caseInfoForTask}
                  onOpenTask={(id) => { void handleOpenTask(id) }}
                  onNewTask={() => { void handleNewTaskForClient() }}
                  onOpenCase={openCaseQuickView}
                  onPatch={patchClientTask}
                  onRemoved={(id) => setClientTasks((rows) => (rows ? rows.filter((t) => t.id !== id) : rows))}
                />
              </section>
            )}
            {/* Documents section removed in increment 2 of the
                Activity/Documents merge. Document rows now render
                inside the Activity timeline (interleaved with activity
                entries by date). "+ Upload document" relocated into the
                Activity section header; per-row Open/Delete errors
                surface via the relocated actionError banner in the
                Activity body. loadDocumentsForClient, handleDownloadDocument,
                handleDeleteDocument, downloadingId/deletingId, and the
                confirmDeleteDoc ConfirmDialogue all remain — the timeline
                consumes them. */}
            {/* ACTIVITY — the billable timeline (ClientActivityFeed): Layout A
                rich cards by default, Layout B rail ledger on toggle, both
                driven by the entry-type registry (constants/entryTypes.ts).
                Data + every mutation stay here — same state and handlers as
                the previous timeline (server filters, drafts, edit/delete/
                view, document open/download/delete, upload, log/email). */}
            {activeClientSection === 'activity' && clientBill && (
              <section className="mb-6">
                <BillView billId={clientBill.id} initialBill={clientBill.initial} backLabel="Activity" onBack={closeClientBill} />
              </section>
            )}
            {activeClientSection === 'activity' && !clientBill && (
              <section className="mb-6">
                <ClientActivityFeed
                  key={selectedClient.id}
                  clientFirstName={selectedClient.forename ?? ''}
                  activities={clientActivities}
                  activitiesAll={clientActivitiesAll}
                  documents={clientDocuments}
                  billEvents={clientBillEvents}
                  refetching={activitiesRefetching}
                  errors={[activityError, documentsError, billEventsError, actionError]}
                  isAdmin={authUser?.role === 'admin'}
                  currentUserId={authUser?.id ?? null}
                  cases={activityCaseOptions}
                  typeFilter={activityTypeFilter}
                  onTypeFilter={(v) => handleActivityTypeFilterChange(v as ActivityTypeFilter)}
                  caseFilter={activityCaseFilter}
                  onCaseFilter={handleActivityCaseFilterChange}
                  corrFilter={activityCorrespondentFilter}
                  onCorrFilter={handleActivityCorrespondentFilterChange}
                  onClearServerFilters={handleClearActivityFilters}
                  drafts={clientDrafts}
                  draftsLoading={!!selectedClient && draftsClientId !== selectedClient.id}
                  onResumeDraft={(d) => { setResumeDraft(d); setShowComposeEmail(true) }}
                  onDeleteDraft={(d) => {
                    const prev = clientDrafts
                    setClientDrafts((rows) => rows.filter((r) => r.id !== d.id))
                    void deleteEmailDraftApi(d.id).catch((err) => {
                      setClientDrafts(prev)
                      setEmailToast(refusalText(err, 'Could not delete the draft.'))
                      window.setTimeout(() => setEmailToast(null), 3000)
                    })
                  }}
                  canModify={(a) => authUser !== null && (authUser.role === 'admin' || authUser.id === a.actorUserId)}
                  deletingActivityId={deletingActivityId}
                  downloadingDocId={downloadingId}
                  deletingDocId={deletingId}
                  onViewActivity={(a) => setViewingActivity(a)}
                  onEditActivity={(a) => setEditingActivity(a)}
                  onDeleteActivity={(a) => setConfirmDeleteActivity(a)}
                  onOpenDocument={(d) => { void handleOpenDocument(d) }}
                  onDownloadDocument={(d) => { void handleDownloadDocument(d) }}
                  onDeleteDocument={(d) => handleDeleteDocument(d)}
                  onOpenCase={(id) => { void handleOpenCase(id) }}
                  onNewActivity={() => { setActivityVariant(null); setShowAddActivityModal(true) }}
                  onLogVariant={(v) => { setActivityVariant(v); setShowAddActivityModal(true) }}
                  onSendEmail={() => setShowComposeEmail(true)}
                  onUpload={() => setShowUploadModal(true)}
                  // The ONE "Invoice" action: a bill is per case, so ask which.
                  onCreateInvoice={handleNewInvoice}
                  onStatement={() => setShowStatement(true)}
                />
                {showStatement && (
                  <ClientStatementModal
                    clientId={selectedClient.id}
                    clientName={[selectedClient.forename, selectedClient.surname].filter(Boolean).join(' ')}
                    onClose={() => setShowStatement(false)}
                  />
                )}
                {pickBillCase && (
                  <ChooseBillCaseModal
                    cases={clientCases === null ? null : activityCaseOptions}
                    clientName={[selectedClient.forename, selectedClient.surname].filter(Boolean).join(' ')}
                    onClose={() => setPickBillCase(false)}
                    onPick={(caseId) => { setPickBillCase(false); startRaiseBill(caseId) }}
                  />
                )}
                {raiseFor && (
                  <RaiseBillModal caseId={raiseFor.caseId} caseName={raiseFor.name} caseRef={raiseFor.ref} data={raiseFor.data}
                    onClose={() => setRaiseFor(null)} onCreated={openClientBill} />
                )}
              </section>
            )}
              </div>
            </div>
            {/* Upload modal — sibling of the SectionWrappers, gated
                on isOpen at the component level. selectedClient guard
                is technically redundant (we're inside the
                `view === 'detail' && selectedClient` fragment) but
                TypeScript's null narrowing doesn't always propagate
                cleanly through nested JSX expressions, and the
                explicit guard is self-documenting. The modal lives
                OUTSIDE the section conditionals so it can stay open
                across section switches (e.g. user clicks + Upload in
                Documents, then accidentally clicks another nav row —
                the modal stays open until they explicitly dismiss). */}
            {selectedClient && (
              <ClientDocumentUploadModal
                isOpen={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                clientId={selectedClient.id}
                clientDisplayName={displayName(selectedClient)}
                cases={clientCasesWithRef}
                onUploadComplete={() => { void loadDocumentsForClient(selectedClient.id) }}
              />
            )}
            {/* Compose-email modal (Microsoft Stage 3). Sends AS the user's
                own mailbox + logs an 'email' activity; on success we refetch
                the timeline (so the new entry appears) + show a brief toast.
                caseId ties the logged activity to the currently-filtered case
                when one is selected, else it's client-level. */}
            {selectedClient && (
              <ComposeEmailModal
                isOpen={showComposeEmail}
                onClose={() => { setShowComposeEmail(false); setResumeDraft(null) }}
                clientId={selectedClient.id}
                clientDisplayName={displayName(selectedClient)}
                caseId={activityCaseFilter || null}
                caseRefLabel={activityCaseFilter ? (clientCasesWithRef.find((c) => c.id === activityCaseFilter)?.ref ?? null) : null}
                // Client-wide tab: pick the case in the window (starts at the
                // feed's case filter). Billable only on a case (NotBillable).
                cases={activityCaseOptions}
                defaultTo={selectedClient.emailAddress || null}
                resumeDraft={resumeDraft}
                onDraftSaved={() => {
                  void loadDraftsForClient(selectedClient.id)
                  setEmailToast('Draft saved.')
                  window.setTimeout(() => setEmailToast(null), 3000)
                }}
                onSent={() => {
                  void loadActivitiesForClient(selectedClient.id, currentActivityFilters())
                  void loadCorrespondentChipSource(selectedClient.id)
                  // A resumed draft is deleted server-side on send — refresh
                  // the strip so the sent draft disappears from the list.
                  void loadDraftsForClient(selectedClient.id)
                  setEmailToast('Email sent.')
                  window.setTimeout(() => setEmailToast(null), 3000)
                }}
              />
            )}
            {emailToast && (
              <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-[220] rounded-full border border-casevo-border bg-casevo-surface shadow-lg px-4 py-2 text-xs text-casevo-forest">
                {emailToast}
              </div>
            )}
            {selectedClient && (
              <AddActivityModal
                isOpen={showAddActivityModal}
                // On close, also clear the variant so the next
                // open — whatever route it takes — starts from a
                // clean null. The individual openers ALSO set
                // activityVariant explicitly, so this is belt-and-
                // braces; both together mean a stale variant can't
                // survive a close.
                onClose={() => {
                  setShowAddActivityModal(false)
                  setActivityVariant(null)
                }}
                clientId={selectedClient.id}
                clientDisplayName={displayName(selectedClient)}
                cases={clientCasesWithRef}
                onCreated={() => { void loadActivitiesForClient(selectedClient.id, currentActivityFilters()); void loadCorrespondentChipSource(selectedClient.id) }}
                variant={activityVariant}
                // Client.mainContactNumber (see src/types/client.
                // types.ts:23). Required string on the interface,
                // so ?? null is defensive rather than strictly
                // necessary — a future backend nullable would flow
                // through without a call-site change.
                clientPhone={selectedClient.mainContactNumber ?? null}
                // Email in the picker is NOT a timed activity — hand off to
                // the SAME compose modal the toolbar Send email button opens
                // (send + rich text + attachments + timer). Close the picker
                // first so we don't stack two modals.
                onPickEmail={() => {
                  setShowAddActivityModal(false)
                  setActivityVariant(null)
                  setShowComposeEmail(true)
                }}
              />
            )}
            {/* Edit-activity modal — separate render from the create
                modal above, driven by `editingActivity` (non-null =
                open, null = closed). Reuses the same AddActivityModal
                component with existingActivity passed in; the modal
                internally switches title/label/API from create to
                edit. onCreated fires on successful PATCH — we clear
                editingActivity + refetch the list so the row shows
                updated fields + the header total recomputes. */}
            {selectedClient && editingActivity && (
              <AddActivityModal
                isOpen={true}
                onClose={() => setEditingActivity(null)}
                clientId={selectedClient.id}
                clientDisplayName={displayName(selectedClient)}
                cases={clientCasesWithRef}
                existingActivity={editingActivity}
                onCreated={() => { setEditingActivity(null); void loadActivitiesForClient(selectedClient.id, currentActivityFilters()); void loadCorrespondentChipSource(selectedClient.id) }}
              />
            )}
            {/* Read-only activity view — opened by the row's "View"
                affordance for users who can't edit (non-author/
                non-admin). Display-only; no save/edit/delete, so no
                refetch on close. */}
            <ActivityViewModal
              isOpen={viewingActivity !== null}
              activity={viewingActivity}
              onClose={() => setViewingActivity(null)}
              isAdmin={authUser?.role === 'admin'}
              canModify={!!viewingActivity && authUser !== null && (authUser.role === 'admin' || authUser.id === viewingActivity.actorUserId)}
              onEdit={(a) => { setViewingActivity(null); setEditingActivity(a) }}
              onDelete={(a) => { setViewingActivity(null); setConfirmDeleteActivity(a) }}
              onRepriced={() => { setViewingActivity(null); if (selectedClient) void loadActivitiesForClient(selectedClient.id, currentActivityFilters()) }}
              caseLabel={viewingActivity?.caseId ? (activityCaseOptions.find((c) => c.id === viewingActivity.caseId) ?? null) : null}
            />
            {/* Delete-activity confirmation. Sibling of the modals
                above so all modal-style dialogs render at the same
                fragment level. Mirrors the document-delete
                ConfirmDialogue's prop shape verbatim (isOpen, title,
                message, confirmLabel with busy-toggle, onConfirm,
                onCancel — no isBusy prop; the busy state is folded
                into the confirmLabel becoming "Deleting..." when
                deletingActivityId is set). */}
            <ConfirmDialogue
              isOpen={confirmDeleteActivity !== null}
              title="Delete activity"
              message={
                confirmDeleteActivity
                  ? `Delete this activity entry (${confirmDeleteActivity.action})? This can't be undone.`
                  : ''
              }
              confirmLabel={deletingActivityId ? 'Deleting...' : 'Delete'}
              onConfirm={() => { void handleConfirmDeleteActivity() }}
              onCancel={() => setConfirmDeleteActivity(null)}
            />
            {/* Delete-document confirmation. Sibling of the upload
                modal so both modal-style dialogs render at the same
                fragment level. confirmLabel toggles to "Deleting..."
                while the API call is in flight (deletingId is set
                immediately after the dialog closes). */}
            <ConfirmDialogue
              isOpen={confirmDeleteDoc !== null}
              title="Delete document?"
              message={
                confirmDeleteDoc
                  ? `${confirmDeleteDoc.filename} will be permanently deleted. If it's attached to a case, it will also be removed from that case's documents. This cannot be undone.`
                  : ''
              }
              confirmLabel={deletingId ? 'Deleting...' : 'Delete'}
              onConfirm={() => { void handleConfirmDelete() }}
              onCancel={() => setConfirmDeleteDoc(null)}
            />
          </div>
        )}
        {/* Case quick-view — rendered for BOTH views: the directory's case chips
            AND the client page (Notes feed linked-case chips). It used to live
            inside the search view only, so a detail-view openCaseQuickView
            fetched the case but nothing rendered. */}
        {chipQuickView && (
          <CaseQuickViewModal
            caseSummary={chipQuickView.summary}
            initialFull={chipQuickView.full}
            onClose={() => setChipQuickView(null)}
            // "Full case →" — the client screen's own open-case path (flush
            // autosaves, caseOpenedFrom 'clientSearch', close the takeover),
            // reusing the modal's fetched row.
            onOpenFull={(id, full) => { setChipQuickView(null); void handleOpenCase(id, full ?? undefined) /* null after an in-modal edit → refetch, never the pre-edit row */ }}
            // The modal's client link → that client's page (same user-click
            // path as the card).
            onOpenClient={(cs) => {
              setChipQuickView(null)
              // Already on this client's page (Notes chip) → just close.
              if (view === 'detail' && selectedClient && cs.client?.id === selectedClient.id) return
              const cl = sortedResults.find((x) => x.id === (cs.client?.id ?? ''))
              if (cl) void navigateWithin(() => { void handleSelectClient(cl) })
            }}
            // In-modal edits (status / name) → patch the chip on the card.
            onPatched={(id, patch) => {
              // (audit) keep the Cases tab / Notes + Activity case labels in step too.
              if (clientCases?.some((c) => c.id === id)) void refreshClientCases()
              const st = useCasevoStore.getState()
              useCasevoStore.setState({
                clients: st.clients.map((cl) => cl.caseSummaries?.some((k) => k.id === id)
                  ? { ...cl, caseSummaries: cl.caseSummaries.map((k) => {
                      if (k.id !== id) return k
                      // Same status rule as the case list: closed > setup-finalised (open) > draft.
                      const closed = 'closeDate' in patch ? !!patch.closeDate : k.status === 'closed'
                      const finalised = 'setupFinalised' in patch ? !!patch.setupFinalised : k.status !== 'draft'
                      const status: 'closed' | 'open' | 'draft' = closed ? 'closed' : finalised ? 'open' : 'draft'
                      return {
                        ...k, status,
                        caseName: 'caseName' in patch ? (patch.caseName ?? null) : k.caseName,
                        // Defendant edits re-derive the chip's shared case name.
                        defendantName: 'defendantName' in patch ? (patch.defendantName ?? null) : k.defendantName,
                      }
                    }) }
                  : cl),
              })
            }}
          />
        )}
      </div>
    </div>
  )
}
export default ClientSearchView
